import React, {useEffect, useState, useCallback, useRef, useMemo} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {App as AntdApp, Button, Collapse, Dropdown, Empty, Input, Popover, Select, Space, Spin, Typography, Radio, Tooltip, InputNumber, Slider} from "antd";
import {
	ArrowLeftOutlined, ZoomInOutlined, ZoomOutOutlined, LeftOutlined, RightOutlined,
	CloudUploadOutlined, UpOutlined, DownOutlined, LinkOutlined, DisconnectOutlined,
	ExpandOutlined, InsertRowLeftOutlined, DeleteOutlined, MenuOutlined, CopyOutlined, ImportOutlined,
	BgColorsOutlined, NumberOutlined, RedoOutlined, SearchOutlined, PlusOutlined, DeleteOutlined as DeleteIcon
} from "@ant-design/icons";
import {Stage, Layer, Image as KonvaImage, Rect} from "react-konva";
import {DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors} from "@dnd-kit/core";
import {arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import api from "@/api/axios.js";
import UploadPdfModal from "../components/UploadPdfModal.jsx";
import AddPagesModal from "../components/AddPagesModal.jsx";
import {PageThumb, SortablePageThumb} from "../components/PageThumb.jsx";
import usePageManagement from "../hooks/usePageManagement.jsx";
import ManualFieldModal from "../components/ManualFieldModal.jsx";
import ManualZoneBadge from "../components/ManualZoneBadge.jsx";
import ZoneContextMenu from "../components/ZoneContextMenu.jsx";
import EditSerialModal from "../components/EditSerialModal.jsx";
import {getZoneSearchState, scrollZoneIntoContainer, zoneSearchHighlightProps} from "../utils/zoneSearchHighlight.js";
import ZoneSerialBadge from "../components/ZoneSerialBadge.jsx";
import ZoneSelectionTransformer from "../components/ZoneSelectionTransformer.jsx";
import ShortcutsLegend from "../components/ShortcutsLegend.jsx";
import {applyZoneGeometryUpdates, clampZoneToPage, getZoneScaleFactor, scaleZonesFromCenter} from "../utils/zoneGroupGeometry.js";
import PageJumper from "../components/PageJumper.jsx";
import {useFieldColorMap, getFieldColor} from "@modules/fields/utils/fieldColors.js";
import {fetchBannerFields} from "@modules/settings/api/settingsApi.js";
import {
	applyManualFieldsToZone,
	collectSharedInitialValues,
	fetchBannerValuesBySerial,
	readBannerZoneValue
} from "@modules/flyers/utils/zoneManualFields.js";
import {resolvePreviewScale} from "@modules/flyers/utils/previewScale.js";
import {formatPdfPageSize} from "@modules/flyers/utils/pdfPageSize.js";
import {useSSE} from "@modules/realtime/hooks/useSSE.js";
import "./VariantPreprocessPage.css";

const {Title, Text} = Typography;

const BASE_RECT_COLORS = {
	// Il colore dice COSA E' la zona, non da dove viene: gabbia e banner restano gli stessi
	// ovunque — pagina del volantino, riferimento, variante. Una gabbia ereditata e' una
	// gabbia come le altre, quindi resta blu: cambia solo se e' stata aggiunta apposta sulla
	// variante che si sta guardando, o se e' in uno stato che va segnalato.
	banner: {stroke: "#22c55e", fill: "rgba(34, 197, 94, 1)"},
	gabbia: {stroke: "#3b82f6", fill: "rgba(59, 130, 246, 1)"},
	// Tracciata ex-novo sulla variante. Ha senso SOLO dove esiste il contrasto con le
	// ereditate, cioe' su una variante: su un volantino normale ogni zona e' "new" per
	// default e colorarle tutte direbbe nulla (vedi il parametro isVariantContext).
	new: {stroke: "#a855f7", fill: "rgba(168, 85, 247, 1)"},
	bannerNew: {stroke: "#06b6d4", fill: "rgba(6, 182, 212, 1)"},
	// Stati che vanno segnalati, indipendenti dal tipo e dalla provenienza. L'ambra e' della
	// stessa famiglia dell'arancio "in elaborazione adesso" (#fa8c16, tratteggiato) perche'
	// dicono cose imparentate: li' l'AI sta lavorando, qui deve ancora farlo.
	reprocess: {stroke: "#f59e0b", fill: "rgba(245, 158, 11, 1)"},
	error: {stroke: "#ff4d4f", fill: "rgba(255, 77, 79, 1)"}
};

// Legenda colori: stessa fonte di verita' di BASE_RECT_COLORS.
const ZONE_LEGEND = [
	{key: "gabbia", label: "Gabbia"},
	{key: "banner", label: "Banner"},
	{key: "new", label: "Gabbia aggiunta qui"},
	{key: "bannerNew", label: "Banner aggiunto qui"},
	{key: "reprocess", label: "Da rielaborare"},
	{key: "error", label: "Errore"}
];

/**
 * `isVariantContext`: il volantino a cui la zona appartiene e' una variante, quindi la
 * distinzione "aggiunta qui" vs "ereditata" e' informativa. Falso per un volantino normale
 * (anche quando fa da riferimento), dove ogni zona e' "new" per default e la distinzione
 * non esiste: li' si resta su blu/verde, come nella sua pagina di pre-elaborazione.
 */
function getZoneColor(zone, isVariantContext = false)
{
	if (zone?.status === "error") return BASE_RECT_COLORS.error;
	if (zone?.status === "reprocess") return BASE_RECT_COLORS.reprocess;
	if (isVariantContext && zone?.status === "new")
		return zone.type === "banner" ? BASE_RECT_COLORS.bannerNew : BASE_RECT_COLORS.new;
	return BASE_RECT_COLORS[zone?.type] || BASE_RECT_COLORS.gabbia;
}

const DEFAULT_SCALE = 1;
const MIN_SPLIT = 0.25;
// Finestra entro cui pressioni di freccia consecutive vengono coalizzate in un unico passo di undo.
const ARROW_COALESCE_MS = 500;

// Margine (px schermo) attorno alla pagina in ciascun canvas, uguale su ogni lato: la pagina
// resta centrata con lo stesso "respiro" a sinistra/destra e sopra/sotto (come in FlyerPreprocessPage).
const STAGE_MARGIN = 30;

function clamp(v, min, max)
{
	return Math.min(Math.max(v, min), max);
}

// Firma strutturale delle zone (id + pagina + tipo + geometria arrotondata): lo storico "Annulla"
// registra un passo solo quando cambia struttura/geometria, ignorando le variazioni dei soli campi
// manuali o dello stato editoriale.
function structuralZonesSig(zones)
{
	return JSON.stringify((zones || []).map((z) => [
		z.id, z.pageIndex, z.type, Math.round(z.x), Math.round(z.y), Math.round(z.w), Math.round(z.h)
	]));
}

function hasSelectionModifier(event)
{
	const e = event?.evt || event;
	return Boolean(e?.ctrlKey || e?.metaKey || e?.shiftKey);
}

// Tipi di <input> che sono widget (checkbox, radio, bottoni...) e non campi di testo: il focus
// su questi elementi non deve bloccare le scorciatoie da tastiera della pagina (Canc, frecce...).
const NON_TEXT_INPUT_TYPES = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file", "image"]);

function isEditableTarget(target)
{
	if (!(target instanceof HTMLElement)) return false;
	if (target.isContentEditable) return true;
	const t = target.tagName;
	if (t === "INPUT")
		return !NON_TEXT_INPUT_TYPES.has((target.type || "text").toLowerCase());
	return t === "TEXTAREA" || t === "SELECT";
}

function readLocalBool(key, fallback)
{
	try
	{
		const v = localStorage.getItem(key);
		if (v === "1") return true;
		if (v === "0") return false;
	}
	catch
	{
	}
	return fallback;
}

function readLocalFloat(key, fallback)
{
	try
	{
		const v = parseFloat(localStorage.getItem(key));
		if (Number.isFinite(v)) return v;
	}
	catch
	{
	}
	return fallback;
}

// ─── TraceableRect (editabile) ───
// Le maniglie di ridimensionamento le disegna ZoneSelectionTransformer, unico per tutta la
// selezione; con più gabbie selezionate il genitore raccoglie le notifiche di fine gesto di
// ciascuna e le applica in un colpo solo.
function TraceableRect({shapeProps, isSelected, onSelect, onChange, style, onContextMenu, onDblClick, searchState = null, zoom = 1})
{
	const shapeRef = useRef();

	return (
		<>
			<Rect
				onClick={onSelect} onTap={onSelect} ref={shapeRef}
				onDblClick={onDblClick} onDblTap={onDblClick}
				onContextMenu={onContextMenu}
				{...shapeProps} {...style}
				shadowColor={isSelected ? style.stroke : undefined}
				shadowBlur={isSelected ? 8 : 0}
				shadowOpacity={isSelected ? 0.6 : 0}
				{...zoneSearchHighlightProps(searchState, zoom)}
				draggable
				onDragEnd={(e) =>
				{
					// Il genitore vincola la gabbia dentro i bordi pagina: se il risultato coincide
					// con la posizione che aveva già (trascinamento tutto fuori pagina), nessuna
					// prop cambia, react-konva non riapplica nulla e il nodo resterebbe disegnato
					// fuori pur essendo salvato dentro. Si riallinea sempre alle prop correnti; se
					// lo stato cambia davvero, è il ri-render a spostarlo.
					const n = e.target;
					const dropped = {x: n.x(), y: n.y()};
					n.position({x: shapeProps.x, y: shapeProps.y});
					onChange({...shapeProps, x: dropped.x, y: dropped.y});
				}}
				onTransformEnd={() =>
				{
					const n = shapeRef.current;
					const sx = n.scaleX(), sy = n.scaleY();
					const resized = {x: n.x(), y: n.y(), width: Math.max(10, n.width() * sx), height: Math.max(10, n.height() * sy)};
					// Stesso riallineamento del trascinamento (vedi sopra).
					n.scaleX(1);
					n.scaleY(1);
					n.position({x: shapeProps.x, y: shapeProps.y});
					n.width(shapeProps.width);
					n.height(shapeProps.height);
					onChange({...shapeProps, ...resized});
				}}
			/>
		</>
	);
}

// ─── SortableZoneItem: singolo elemento zona nella sidebar gabbie ───
function SortableZoneItem({zone, isSelected, onSelect, onDelete})
{
	const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: zone.id});
	const style = {transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 100 : 1};

	return (
		<div ref={setNodeRef} style={style} className={`vpp-zone-item ${isSelected ? "selected" : ""}`} onClick={onSelect}>
			<div className="vpp-zone-drag" {...attributes} {...listeners}>
				<MenuOutlined style={{color: "#bfbfbf", fontSize: 10}}/>
			</div>
			<div className="vpp-zone-dot" style={{backgroundColor: getZoneColor(zone, true).stroke}}/>
			<Text strong style={{fontSize: 11}}>#{zone.id}</Text>
			{isSelected && (
				<DeleteOutlined style={{color: "#ff4d4f", fontSize: 10, marginLeft: "auto"}} onClick={(e) =>
				{
					e.stopPropagation();
					onDelete(zone.id);
				}}/>
			)}
		</div>
	);
}

/**
 * Risultato di ricerca: stessa card di SortableZoneItem (pallino di stato, seriale,
 * eliminazione da selezionata) senza la maniglia di trascinamento — il riordino vale
 * dentro una pagina, i risultati attraversano tutta la variante. In più pagina e prodotto.
 */
function ZoneResultItem({zone, isSelected, onSelect, onDelete, description, isVariantContext = true})
{
	return (
		<div className={`vpp-zone-item vpp-zone-result ${isSelected ? "selected" : ""}`} onClick={onSelect}>
			<div className="vpp-zone-dot" style={{backgroundColor: getZoneColor(zone, isVariantContext).stroke}}/>
			<div className="vpp-zone-result-info">
				<div className="vpp-zone-result-head">
					<Text strong style={{fontSize: 11}}>#{zone.id}</Text>
					<Text type="secondary" style={{fontSize: 10}}>P{zone.pageIndex + 1} ({zone.type})</Text>
				</div>
				{description && (
					<Text type="secondary" className="vpp-zone-result-desc" title={description}>{description}</Text>
				)}
			</div>
			{isSelected && onDelete && (
				<DeleteIcon style={{color: "#ff4d4f", fontSize: 10}} onClick={(e) =>
				{
					e.stopPropagation();
					onDelete(zone.id);
				}}/>
			)}
		</div>
	);
}

function reorderZonesWithinPage(items, pageIndex, activeId, overId)
{
	const pageIds = items.filter(z => z.pageIndex === pageIndex).map(z => z.id);
	const oldIdx = pageIds.indexOf(activeId);
	const newIdx = pageIds.indexOf(overId);
	if (oldIdx === -1 || newIdx === -1) return items;
	const reordered = arrayMove(pageIds, oldIdx, newIdx);
	const reorderedZones = reordered.map(id => items.find(z => z.id === id)).filter(Boolean);
	let cursor = 0;
	return items.map(z =>
	{
		if (z.pageIndex !== pageIndex) return z;
		return reorderedZones[cursor++] || z;
	});
}

// ─── Hook: carica pagine e immagini di un flyer ───
function useFlyerPages(flyerId)
{
	const [loading, setLoading] = useState(true);
	const [pages, setPages] = useState([]);
	const [zones, setZones] = useState([]);
	const [previewScale, setPreviewScale] = useState(1);
	// Larghezza raster vera della pagina (0 se il server non la manda): unita' di misura per
	// ricavare la scala effettiva dal rapporto con l'anteprima caricata, invece di fidarsi
	// del previewScale salvato che puo' non corrispondere piu' ai file di anteprima su disco.
	const [rasterPageWidth, setRasterPageWidth] = useState(0);
	const [loadedImages, setLoadedImages] = useState({});
	const [config, setConfig] = useState({});
	const [pageWidth, setPageWidth] = useState(0);
	const [pageHeight, setPageHeight] = useState(0);
	// Solo sulle varianti: {etichetta pagina variante → etichetta pagina riferimento}.
	const [parentPageLabels, setParentPageLabels] = useState(null);
	// Volantino da cui le zone in stato provengono davvero: valorizzato SOLO a fetch riuscita.
	// Serve all'autosave per non scrivere uno stato che non è mai stato caricato (vedi sotto).
	const loadedFlyerIdRef = useRef(null);

	const fetchData = useCallback(async () =>
	{
		if (!flyerId) return;
		setLoading(true);
		setLoadedImages({});
		try
		{
			const res = await api.get(`/api/flyers/${flyerId}/preprocess`);
			if (res.data?.ok)
			{
				const nextPages = res.data.pages || [];
				setPages(nextPages);
				setZones(res.data.preprocessZones || []);
				setPreviewScale(res.data.previewScale || 1);
				setRasterPageWidth(res.data.rasterPageWidth || 0);
				if (res.data.pageWidth) setPageWidth(res.data.pageWidth);
				if (res.data.pageHeight) setPageHeight(res.data.pageHeight);
				if (res.data.preprocessConfig) setConfig(res.data.preprocessConfig);
				setParentPageLabels(res.data.parentPageLabels || null);
				loadedFlyerIdRef.current = flyerId;
				if (!nextPages.length) setLoading(false);
			}
		}
		catch
		{
			setLoading(false);
		}
	}, [flyerId]);

	useEffect(() =>
	{
		fetchData();
	}, [fetchData]);

	useEffect(() =>
	{
		if (!pages.length) return;
		let count = 0;
		const imgs = {};
		const base = import.meta.env.VITE_API_URL || "";
		pages.forEach((page) =>
		{
			const img = new Image();
			img.crossOrigin = "anonymous";
			const url = page.previewUrl || page.url;
			img.src = url.startsWith("http") ? url : `${base}${url}`;
			const done = () =>
			{
				count++;
				if (count === pages.length)
				{
					setLoadedImages(imgs);
					setLoading(false);
				}
			};
			img.onload = () =>
			{
				imgs[page.index] = img;
				done();
			};
			img.onerror = () =>
			{
				done();
			};
		});
	}, [pages]);

	return {loading, pages, zones, setZones, previewScale, rasterPageWidth, loadedImages, config, pageWidth, pageHeight, parentPageLabels, loadedFlyerIdRef, refetch: fetchData};
}

// ═══════════════════════════════════════════════════════════════
// PAGINA PRINCIPALE
// ═══════════════════════════════════════════════════════════════
export default function VariantPreprocessPage()
{
	const {variantId} = useParams();
	const navigate = useNavigate();
	const {message, modal} = AntdApp.useApp();

	// ─── Stato flyer ───
	const [variantFlyer, setVariantFlyer] = useState(null);
	const [parentFlyerId, setParentFlyerId] = useState(null);
	// Opzioni di riferimento: tutti i volantini/varianti validati (DONE/READY) della promo
	const [referenceOptions, setReferenceOptions] = useState([]);

	useEffect(() =>
	{
		if (!variantId) return;
		api.get(`/api/flyers/${variantId}`).then(({data}) =>
		{
			const f = data?.flyer || data;
			setVariantFlyer(f);
			setParentFlyerId(f?.parentFlyerId || null);
		}).catch(() => message.error("Errore caricamento variante"));
	}, [variantId, message]);

	// Carica i riferimenti validati della promo per la select del pannello sinistro
	useEffect(() =>
	{
		const promoId = variantFlyer?.promoId;
		if (!promoId) return;
		api.get("/api/flyers", {params: {promoId, limit: 500}}).then(({data}) =>
		{
			const items = (data?.items || data || [])
				.filter(f => (f._id || f.id) !== variantId)
				.filter(f => f.status === "DONE" || f.status === "READY")
				.map(f => ({
					label: `${f.name || "Volantino"}${f.isVariant ? " (variante)" : ""}`,
					value: String(f._id || f.id),
					isVariant: !!f.isVariant
				}));
			setReferenceOptions(items);
		}).catch(() => setReferenceOptions([]));
	}, [variantFlyer?.promoId, variantId]);

	// Il riferimento e' a sua volta una variante? Solo in quel caso ha senso distinguere le
	// gabbie che ha aggiunto lei da quelle che ha ereditato: se e' un volantino normale, o se
	// non lo sappiamo ancora, resta sul neutro blu/verde come nella sua pagina.
	const parentIsVariant = useMemo(
		() => !!referenceOptions.find(o => o.value === String(parentFlyerId))?.isVariant,
		[referenceOptions, parentFlyerId]
	);

	// Prenota atomicamente `count` seriali promo-unici dal server (fonte di verità).
	// Solo le gabbie NUOVE consumano seriali; le ereditate mantengono quello sorgente.
	// Ritorna {status: "ok"|"no-promo"|"error", serials}.
	const reserveSerials = useCallback(async (count = 1) =>
	{
		const promoId = variantFlyer?.promoId;
		if (!promoId) return {status: "no-promo", serials: []};
		try
		{
			const {data} = await api.post(`/api/promos/${promoId}/reserve-serials`, {count});
			if (data?.ok && Array.isArray(data.serials) && data.serials.length === count)
				return {status: "ok", serials: data.serials};
			return {status: "error", serials: []};
		}
		catch
		{
			return {status: "error", serials: []};
		}
	}, [variantFlyer?.promoId]);

	// ─── Carica pagine padre e variante ───
	const parent = useFlyerPages(parentFlyerId);
	const variant = useFlyerPages(variantId);
	const variantPdfPageSize = formatPdfPageSize(variant.config);

	// ─── Pagina corrente indipendente per lato ───
	const [parentPageIdx, setParentPageIdx] = useState(0);
	const [variantPageIdx, setVariantPageIdx] = useState(0);

	useEffect(() =>
	{
		if (parent.pages.length) setParentPageIdx(0);
	}, [parent.pages.length]);
	useEffect(() =>
	{
		if (variant.pages.length) setVariantPageIdx(0);
	}, [variant.pages.length]);

	// ─── Zoom indipendente con persistenza ───
	const [parentZoom, setParentZoom] = useState(() => readLocalFloat("vpp.zoom.parent", DEFAULT_SCALE));
	const [variantZoom, setVariantZoom] = useState(() => readLocalFloat("vpp.zoom.variant", DEFAULT_SCALE));

	// ─── Ricerca gabbie su TUTTA la variante ───
	// Stessa logica della pre-elaborazione volantino: i testi prodotto stanno nelle FlyerCard,
	// non nelle zone, quindi si caricano alla prima ricerca. Guardia sincrona con ref contro
	// le richieste doppie (lo stato di caricamento si aggiorna solo dopo il render).
	const [zoneQuery, setZoneQuery] = useState("");
	const [cardTextBySerial, setCardTextBySerial] = useState(null);
	const [cardTextLoading, setCardTextLoading] = useState(false);
	const cardTextFetchRef = useRef(false);

	// ─── Ricerca gabbie sul riferimento (sola lettura, stessa logica della variante) ───
	const [parentZoneQuery, setParentZoneQuery] = useState("");
	const [parentCardTextBySerial, setParentCardTextBySerial] = useState(null);
	const [parentCardTextLoading, setParentCardTextLoading] = useState(false);
	const parentCardTextFetchRef = useRef(false);

	// ─── Seriali sul canvas (riferimento + variante), preferenza persistente ───
	const [showSerials, setShowSerials] = useState(() => readLocalBool("vpp.showSerials", true));

	useEffect(() =>
	{
		try
		{
			localStorage.setItem("vpp.showSerials", showSerials ? "1" : "0");
		}
		catch
		{
		}
	}, [showSerials]);

	useEffect(() =>
	{
		try
		{
			localStorage.setItem("vpp.zoom.parent", String(parentZoom));
		}
		catch
		{
		}
	}, [parentZoom]);
	useEffect(() =>
	{
		try
		{
			localStorage.setItem("vpp.zoom.variant", String(variantZoom));
		}
		catch
		{
		}
	}, [variantZoom]);

	// ─── Sincronizzazione pagine ───
	const [syncPages, setSyncPages] = useState(false);

	// L'accoppiamento fra i due pannelli è per CONTENUTO, non per posizione: ogni pagina
	// della variante resta legata alla pagina del riferimento da cui è nata anche dopo che
	// riordini o inserimenti ne hanno cambiato il numero. La corrispondenza è fissata dal
	// server per etichette (parentPageLabels), qui si risolvono le etichette sulle pagine
	// effettivamente caricate. Una pagina aggiunta dopo, o il cui corrispondente è stato
	// eliminato, non compare nella mappa: il pannello opposto resta dov'è.
	const parentIdxByVariantIdx = useMemo(() =>
	{
		const pairs = new Map();
		if (!variant.parentPageLabels) return pairs;

		const parentIdxByLabel = new Map(parent.pages.map((page, idx) => [String(page.label), idx]));
		variant.pages.forEach((page, idx) =>
		{
			const parentLabel = variant.parentPageLabels[String(page.label)];
			if (parentLabel == null) return;
			const parentIdx = parentIdxByLabel.get(String(parentLabel));
			if (parentIdx !== undefined) pairs.set(idx, parentIdx);
		});
		return pairs;
	}, [variant.parentPageLabels, variant.pages, parent.pages]);

	// Più pagine della variante possono nascere dalla stessa pagina del riferimento (una
	// pagina duplicata): nella direzione inversa vince la prima, l'unica scelta stabile.
	const variantIdxByParentIdx = useMemo(() =>
	{
		const pairs = new Map();
		for (const [variantIdx, parentIdx] of parentIdxByVariantIdx)
			if (!pairs.has(parentIdx)) pairs.set(parentIdx, variantIdx);
		return pairs;
	}, [parentIdxByVariantIdx]);

	// Porta la variante alla pagina indicata e allinea il riferimento al suo corrispondente.
	const goToVariantPage = useCallback((idx) =>
	{
		setVariantPageIdx(idx);
		const paired = parentIdxByVariantIdx.get(idx);
		if (paired !== undefined) setParentPageIdx(paired);
	}, [parentIdxByVariantIdx]);

	// Freccia su:
	//   sync ON  → arretra la variante, il riferimento la segue sul suo corrispondente
	//   sync OFF → converge (avvicina chi è più avanti verso l'altro)
	const handleSyncPageUp = useCallback(() =>
	{
		if (syncPages)
		{
			if (variantPageIdx > 0) goToVariantPage(variantPageIdx - 1);
		}
		else
		{
			if (variantPageIdx > parentPageIdx)
			{
				setVariantPageIdx(i => i - 1);
			}
			else if (parentPageIdx > variantPageIdx)
			{
				setParentPageIdx(i => i - 1);
			}
			else if (parentPageIdx > 0)
			{
				setParentPageIdx(i => i - 1);
				setVariantPageIdx(i => i - 1);
			}
		}
	}, [syncPages, parentPageIdx, variantPageIdx, goToVariantPage]);

	// Freccia giù:
	//   sync ON  → avanza la variante, il riferimento la segue sul suo corrispondente
	//   sync OFF → avanza solo variante
	const handleSyncPageDown = useCallback(() =>
	{
		const vMax = Math.max(0, variant.pages.length - 1);
		if (syncPages)
		{
			if (variantPageIdx < vMax) goToVariantPage(variantPageIdx + 1);
		}
		else
		{
			if (variantPageIdx < vMax) setVariantPageIdx(i => i + 1);
		}
	}, [syncPages, variantPageIdx, variant.pages.length, goToVariantPage]);

	// Sync: quando attivo, cambiare pagina su un lato porta l'altro sulla pagina accoppiata
	const changeParentPage = useCallback((idx) =>
	{
		setParentPageIdx(idx);
		if (!syncPages) return;
		const paired = variantIdxByParentIdx.get(idx);
		if (paired !== undefined) setVariantPageIdx(paired);
	}, [syncPages, variantIdxByParentIdx]);

	const changeVariantPage = useCallback((idx) =>
	{
		setSelectedIds([]);
		if (syncPages)
		{
			goToVariantPage(idx);
			return;
		}
		setVariantPageIdx(idx);
	}, [syncPages, goToVariantPage]);

	// ─── Adatta entrambi ───
	const parentScrollRef = useRef(null);
	const parentStageRef = useRef();
	const parentPreviewRectRef = useRef(null);
	// Cmd/Ctrl/Shift + trascina su area vuota del riferimento: selezione multipla a
	// rettangolo. Stato indipendente da quello del pannello variante (isMarqueeSelecting),
	// il riferimento è un pannello a sé, sola lettura.
	const isParentMarqueeSelecting = useRef(false);
	const parentStartPos = useRef(null);
	const variantScrollRef = useRef();

	// ─── Pagine correnti ───
	const parentPage = parent.pages[parentPageIdx];
	const parentImage = parentPage ? parent.loadedImages[parentPage.index] : null;
	const variantPage = variant.pages[variantPageIdx];
	const variantImage = variantPage ? variant.loadedImages[variantPage.index] : null;

	// ─── Spazio di disegno ───
	// Entrambi i canvas lavorano in coordinate PAGINA (full-res), le stesse in cui sono
	// memorizzate le gabbie: l'immagine di anteprima viene disegnata alla dimensione della
	// pagina reale invece che alla propria dimensione in pixel. Senza questo, due volantini
	// rasterizzati con previewScale diversi (es. padre 1, variante 0.5) allo stesso "30%"
	// venivano disegnati a dimensioni diverse — e ogni misura di interfaccia espressa in
	// coordinate scena (spessore bordi, badge, maniglie del Transformer) risultava doppia
	// da un lato. Con un unico spazio condiviso la conversione non serve piu'.
	// La scala di ciascun lato non si legge dal previewScale salvato a DB: si ricava dal
	// rapporto fra l'anteprima realmente caricata e la larghezza raster del volantino,
	// perche' il valore salvato puo' non corrispondere piu' ai file su disco (anteprime
	// riscritte dopo il caricamento del PDF) — in quel caso la pagina sarebbe risultata
	// larga il doppio e le gabbie disegnate a meta' in alto a sinistra. Il previewScale
	// del server resta il ripiego (immagine non ancora caricata o riferimento assente).
	const vPreviewScale = useMemo(
		() => resolvePreviewScale(variantImage, variant.rasterPageWidth, variant.previewScale),
		[variantImage, variant.rasterPageWidth, variant.previewScale]
	);
	const pPreviewScale = useMemo(
		() => resolvePreviewScale(parentImage, parent.rasterPageWidth, parent.previewScale),
		[parentImage, parent.rasterPageWidth, parent.previewScale]
	);

	const handleFitBoth = useCallback(() =>
	{
		const fitZoom = (scrollEl, image, effectiveScale) =>
		{
			if (!scrollEl || !image) return DEFAULT_SCALE;
			const cw = scrollEl.clientWidth - 40;
			const ch = scrollEl.clientHeight - 40;
			// image.width e' in pixel ANTEPRIMA: va riportato a pixel pagina con la STESSA scala
			// effettiva dei canvas (vPreviewScale/pPreviewScale), perche' lo zoom restituito deve
			// restare nella stessa unita' mostrata in testata (% della pagina reale).
			const ps = effectiveScale && effectiveScale > 0 ? effectiveScale : 1;
			const zw = cw / (image.width / ps);
			const zh = ch / (image.height / ps);
			return Math.max(0.1, Math.min(2, Math.min(zw, zh)));
		};
		setParentZoom(fitZoom(parentScrollRef.current, parentImage, pPreviewScale));
		setVariantZoom(fitZoom(variantScrollRef.current, variantImage, vPreviewScale));
	}, [parentImage, variantImage, pPreviewScale, vPreviewScale]);

	// ─── Split ratio ───
	const [splitRatio, setSplitRatio] = useState(0.5);
	const splitContainerRef = useRef(null);
	const isDraggingDivider = useRef(false);

	const handleDividerMouseDown = useCallback((e) =>
	{
		e.preventDefault();
		isDraggingDivider.current = true;
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
	}, []);

	useEffect(() =>
	{
		const onMove = (e) =>
		{
			if (!isDraggingDivider.current || !splitContainerRef.current) return;
			const rect = splitContainerRef.current.getBoundingClientRect();
			const ratio = (e.clientX - rect.left) / rect.width;
			setSplitRatio(clamp(ratio, MIN_SPLIT, 1 - MIN_SPLIT));
		};
		const onUp = () =>
		{
			if (isDraggingDivider.current)
			{
				isDraggingDivider.current = false;
				document.body.style.cursor = "";
				document.body.style.userSelect = "";
			}
		};
		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
		return () =>
		{
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
		};
	}, []);

	// ─── DnD sensors ───
	const dndSensors = useSensors(
		useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
		useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
	);

	// ─── Variant editing state ───
	const [selectedIds, setSelectedIds] = useState([]);
	const [activeType, setActiveType] = useState("gabbia");
	const [rectStrokeWidth, setRectStrokeWidth] = useState(3);
	const [rectOpacity, setRectOpacity] = useState(0.3);
	const [saving, setSaving] = useState(false);
	const [pdfModalOpen, setPdfModalOpen] = useState(false);
	// Context menu tasto destro su una gabbia variante: {x, y, targetIds:number[]} | null
	const [zoneMenu, setZoneMenu] = useState(null);
	// Gabbia in rinumerazione manuale (modale "Modifica seriale"), null = chiusa.
	const [serialEditZone, setSerialEditZone] = useState(null);
	// Campi "manuali" della cooperativa (allowed in /org con AI effettiva = false) mostrati nel menu contestuale.
	const [manualFields, setManualFields] = useState([]);
	// Campi delle gabbie banner (tutti, per le etichette dei tag sulle gabbie); nel menu
	// contestuale e nella modale compaiono solo quelli visibili al tasto destro.
	const [bannerFields, setBannerFields] = useState([]);
	const bannerMenuFields = useMemo(() => bannerFields.filter((f) => f.contextMenu), [bannerFields]);
	// Mappa chiave→nome campo, per etichettare i tag sulle gabbie ("Nome campo: valore").
	// Le stesse chiavi possono avere nomi diversi sulle due superfici (es. banner_video è un
	// campo prodotto per alcuni clienti): ogni gabbia usa prima i nomi del proprio tipo.
	const fieldNamesByZoneType = useMemo(() =>
	{
		const toMap = (list) => Object.fromEntries(list.filter((f) => f?.key).map((f) => [f.key, f.name || f.key]));
		const productNames = toMap(manualFields);
		const bannerNames = toMap(bannerFields);
		return {
			gabbia: {...bannerNames, ...productNames},
			banner: {...productNames, ...bannerNames}
		};
	}, [manualFields, bannerFields]);
	// Colore configurato in Impostazioni > Descrizioni AI per ciascun campo, per i tag
	// sulle gabbie (stessa fonte usata da editor card/gestione volantini/selezione massiva).
	const fieldColorMap = useFieldColorMap();
	const fieldColorByKey = useMemo(() =>
	{
		const map = {};
		for (const f of [...manualFields, ...bannerFields])
			if (f?.key)
				map[f.key] = getFieldColor(fieldColorMap, f.key);
		return map;
	}, [manualFields, bannerFields, fieldColorMap]);
	// Modale di inserimento valore manuale: {field, targetIds:number[], initialValue} | null
	const [manualModal, setManualModal] = useState(null);

	const [parentSelectedIds, setParentSelectedIds] = useState([]);
	const parentSelectedIdsSet = useMemo(() => new Set(parentSelectedIds), [parentSelectedIds]);

	// Reset selezione padre al cambio pagina
	useEffect(() => { setParentSelectedIds([]); }, [parentPageIdx]);

	// ─── Doppio click su zona variante: toggle stato rielaborazione AI (inherited ↔ reprocess, error → reprocess) ───
	const handleZoneToggleStatus = useCallback((zoneId, e) =>
	{
		const evt = e?.evt || e;
		evt?.preventDefault?.();
		const zone = variant.zones.find(z => z.id === zoneId);
		if (!zone || zone.type === "banner" || zone.status === "new") return;
		// inherited ↔ reprocess, error → reprocess
		const nextStatus = zone.status === "reprocess" ? "inherited" : "reprocess";
		variant.setZones(prev => prev.map(z => z.id === zoneId ? {...z, status: nextStatus} : z));
	}, [variant]);

	const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);

	// ─── Tasto destro su zona variante: apre il menu contestuale (campi manuali per le gabbie
	// prodotto, oppure i soli campi banner per le gabbie banner) ───
	const handleZoneContextMenu = useCallback((zoneId, e) =>
	{
		const evt = e?.evt || e;
		evt?.preventDefault?.();
		const zone = variant.zones.find(z => z.id === zoneId);
		if (!zone) return;

		const isBannerZone = zone.type === "banner";
		if (!isBannerZone && zone.type !== "gabbia") return; // tipo zona non gestito dal menu

		// Se il tasto destro cade su una zona già in multi-selezione, il bersaglio è l'intera
		// selezione (stesso tipo); altrimenti la singola zona (e vi allinea la selezione).
		const inSelection = selectedIdsSet.has(zoneId) && selectedIds.length > 1;
		const targetIds = inSelection
			? variant.zones.filter(z => selectedIdsSet.has(z.id) && z.type === zone.type).map(z => z.id)
			: [zoneId];
		if (!inSelection) setSelectedIds([zoneId]);

		// Bersaglio della rielaborazione AI: SEMPRE le sole gabbie dell'intera selezione, non
		// filtrate per il tipo su cui è caduto il tasto destro come targetIds qui sopra. Senza
		// questa distinzione, selezionando gabbie+banner insieme e cliccando col destro su un
		// banner, "Rielabora" spariva del tutto anche se la selezione conteneva gabbie da
		// rielaborare — i banner non passano comunque mai dall'AI.
		const reprocessTargetIds = inSelection
			? variant.zones.filter(z => selectedIdsSet.has(z.id) && z.type === "gabbia").map(z => z.id)
			: (isBannerZone ? [] : [zoneId]);

		setZoneMenu({
			x: evt.clientX, y: evt.clientY,
			targetIds, reprocessTargetIds,
			kind: isBannerZone ? "banner" : "gabbia"
		});
	}, [variant, selectedIds, selectedIdsSet]);

	// Apre la modale unificata dei campi manuali per il set di campi del tipo di zona (gabbia
	// prodotto o banner); precompila ogni campo se le gabbie bersaglio condividono lo stesso valore.
	// Sulle gabbie banner il valore viene dalla card (vedi readBannerZoneValue).
	const openManualFields = useCallback(async (targetIds, kind) =>
	{
		const fieldList = kind === "banner" ? bannerMenuFields : manualFields;
		if (!Array.isArray(targetIds) || !targetIds.length || !fieldList.length)
			return;

		const findZone = (id) => variant.zones.find(z => z.id === id);
		let readValue = (id, key) => findZone(id)?.manualOverrides?.[key];
		if (kind === "banner")
		{
			const bannerBySerial = await fetchBannerValuesBySerial(variantId);
			readValue = (id, key) => readBannerZoneValue(findZone(id), bannerBySerial, key);
		}

		const initialValues = collectSharedInitialValues(fieldList, targetIds, readValue);
		setManualModal({fields: fieldList, targetIds, initialValues});
	}, [manualFields, bannerMenuFields, variant, variantId]);

	// Applica i campi modificati nella modale alle gabbie bersaglio in una sola chiamata batch:
	// persiste sul backend e aggiorna lo stato locale delle zone con i campi che il backend ha
	// davvero salvato. In caso di errore la modale resta aperta, così il valore non va perso.
	const handleApplyManual = useCallback(async (fieldsPayload) =>
	{
		const current = manualModal;
		if (!current || !Array.isArray(fieldsPayload) || !fieldsPayload.length)
			return;
		const {targetIds} = current;

		try
		{
			const {data} = await api.put(`/api/flyers/${variantId}/zone-manual-fields`, {
				zoneIds: targetIds,
				fields: fieldsPayload
			});
			if (data?.ok === false)
			{
				message.error(data.error || "Errore durante l'applicazione");
				return;
			}

			const savedFields = Array.isArray(data?.fields) ? data.fields : fieldsPayload;
			const labelByKey = new Map(fieldsPayload.map(({fieldKey, label}) => [fieldKey, label]));
			variant.setZones(prev => prev.map(z => (targetIds.includes(z.id)
				? applyManualFieldsToZone(z, savedFields, labelByKey)
				: z)));

			message.success(`Campi applicati a ${targetIds.length} gabbia/e`);
			setManualModal(null);
		}
		catch (err)
		{
			message.error(err?.response?.data?.error || "Errore durante l'applicazione");
		}
	}, [manualModal, variantId, variant, message]);

	// Carica i campi "manuali" (allowed con AI effettiva = false) della cooperativa della variante.
	useEffect(() =>
	{
		const cooperativaId = variantFlyer?.cooperativaId;
		if (!cooperativaId)
		{
			setManualFields([]);
			return undefined;
		}

		let active = true;
		api.get(`/api/orgnodes/${cooperativaId}/manual-fields`)
			.then(({data}) => { if (active) setManualFields(Array.isArray(data?.fields) ? data.fields : []); })
			.catch(() => { if (active) setManualFields([]); });

		return () => { active = false; };
	}, [variantFlyer?.cooperativaId]);

	// Campi delle gabbie banner: uguali per tutti i clienti, con la scelta di quali compaiono al
	// tasto destro (Impostazioni → Descrizione banner).
	useEffect(() =>
	{
		let active = true;
		fetchBannerFields()
			.then((fields) => { if (active) setBannerFields(fields); })
			.catch(() => { if (active) setBannerFields([]); });

		return () => { active = false; };
	}, []);

	// Chiude il context menu al cambio pagina variante (coordinate viewport stale).
	useEffect(() => { setZoneMenu(null); }, [variantPageIdx]);

	// Chiusura del context menu su click/tasto destro esterni, scroll o Escape (Dropdown con trigger=[]).
	useEffect(() =>
	{
		if (!zoneMenu) return undefined;

		const closeIfOutside = (e) =>
		{
			if (e.target?.closest?.(".zone-ctx-menu")) return;
			setZoneMenu(null);
		};
		const closeNow = () => setZoneMenu(null);
		const onKey = (e) => { if (e.key === "Escape") setZoneMenu(null); };

		window.addEventListener("mousedown", closeIfOutside, true);
		window.addEventListener("contextmenu", closeIfOutside, true);
		window.addEventListener("scroll", closeNow, true);
		window.addEventListener("keydown", onKey);

		return () =>
		{
			window.removeEventListener("mousedown", closeIfOutside, true);
			window.removeEventListener("contextmenu", closeIfOutside, true);
			window.removeEventListener("scroll", closeNow, true);
			window.removeEventListener("keydown", onKey);
		};
	}, [zoneMenu]);

	// ─── Variant drawing refs ───
	const variantStageRef = useRef();
	const previewRectRef = useRef(null);
	const isDrawing = useRef(false);
	// Cmd/Ctrl/Shift + trascina su area vuota: selezione multipla a rettangolo (marquee).
	// Stato separato da isDrawing perché è un gesto diverso (seleziona, non traccia).
	const isMarqueeSelecting = useRef(false);
	const startPos = useRef(null);
	const clipboardRef = useRef(null);
	const lastPointerRef = useRef(null);

	// Storico "Annulla"/"Rifai" (Cmd/Ctrl+Z e Cmd/Ctrl+Shift+Z) delle zone della VARIANTE: due pile
	// di snapshot; registra solo i cambi strutturali (creazione/spostamento/ridimensionamento/
	// eliminazione/incolla/riordino). restoringRef evita che il ripristino (undo o redo) venga
	// registrato di nuovo; ogni NUOVA azione svuota redoStackRef.
	const undoStackRef = useRef([]);
	const redoStackRef = useRef([]);
	const prevZonesRef = useRef([]);
	const restoringRef = useRef(false);
	// Specchio dell'ultimo variant.zones renderizzato: quando torna la risposta dell'autosave
	// serve sapere se nel frattempo l'utente ha toccato altro, e la closure del timer vede per
	// forza il valore inviato, non quello corrente.
	const zonesRef = useRef([]);
	// Coalescing delle frecce: suppressHistoryRef fa saltare all'effetto la registrazione dei
	// movimenti intermedi; arrowBurstRef indica una sequenza di frecce in corso; arrowTimerRef la
	// chiude dopo una breve pausa. Così un'intera sequenza di frecce = un solo passo di undo.
	// Vale anche per la scala da tastiera (Cmd+. / Cmd+,): burstKindRef distingue le due
	// sequenze, così frecce e scala ravvicinate restano due passi di undo separati.
	const suppressHistoryRef = useRef(false);
	const arrowBurstRef = useRef(false);
	const arrowTimerRef = useRef(null);
	const burstKindRef = useRef(null);
	// Geometrie in arrivo dalla fine di un trascinamento/ridimensionamento sul canvas, raccolte
	// per gabbia e applicate insieme (vedi queueZoneGeometry).
	const pendingGeometryRef = useRef(new Map());

	// Le conversioni anteprima↔pagina sono l'identità: il canvas lavora già in coordinate
	// pagina (spazio di disegno unificato, vedi sopra), le gabbie non vanno riscalate.
	const toPreview = useCallback((v) => v, []);
	const toFull = useCallback((v) => v, []);
	const pToPreview = useCallback((v) => v, []);

	// ─── Dimensioni pagina reale (l'anteprima e' solo una rasterizzazione piu' o meno fitta) ───
	// Si divide per la scala effettiva ricavata dal rapporto fra anteprima caricata e larghezza
	// raster (vedi "Spazio di disegno"), non per il previewScale salvato a DB che puo' essere
	// rimasto vecchio rispetto ai file su disco.
	const vPageW = (variantImage?.width || 1000) / vPreviewScale;
	const vPageH = (variantImage?.height || 1000) / vPreviewScale;
	const pPageW = (parentImage?.width || 1000) / pPreviewScale;
	const pPageH = (parentImage?.height || 1000) / pPreviewScale;

	// Gabbie della variante agganciate al riquadro di trasformazione: le selezionate della
	// pagina a schermo.
	const transformerZoneIds = useMemo(
		() => variant.zones
			.filter((z) => z.pageIndex === variantPageIdx && selectedIdsSet.has(z.id))
			.map((z) => String(z.id)),
		[variant.zones, variantPageIdx, selectedIdsSet]
	);
	// Fine trascinamento/ridimensionamento sul canvas: con più gabbie nel riquadro Konva notifica
	// ciascuna separatamente, nello stesso gesto. Le geometrie si raccolgono e si applicano dopo
	// l'ultima notifica, in un solo aggiornamento: un solo passo di Annulla, e il vincolo dentro
	// la pagina vale per il gruppo intero (le gabbie non si schiacciano contro il bordo).
	const setVariantZones = variant.setZones;
	const queueZoneGeometry = useCallback((zoneId, geometry) =>
	{
		const pending = pendingGeometryRef.current;
		if (!pending.size)
		{
			queueMicrotask(() =>
			{
				const updates = new Map(pendingGeometryRef.current);
				pendingGeometryRef.current.clear();
				setVariantZones((prev) => applyZoneGeometryUpdates(prev, updates, vPageW, vPageH));
			});
		}
		pending.set(zoneId, geometry);
	}, [setVariantZones, vPageW, vPageH]);

	// ─── Stage dimensions ───
	// Lo zoom e' quindi una percentuale della PAGINA: 30% disegna la stessa dimensione sui
	// due lati, che e' il presupposto del confronto affiancato.
	const vStageW = vPageW * variantZoom + STAGE_MARGIN * 2;
	const vStageH = vPageH * variantZoom + STAGE_MARGIN * 2;
	const pStageW = pPageW * parentZoom + STAGE_MARGIN * 2;
	const pStageH = pPageH * parentZoom + STAGE_MARGIN * 2;

	// ─── Preview style for drawing ───
	const previewStyle = useMemo(() =>
	{
		// Sulla variante la zona disegnata nasce sempre con status "new": l'anteprima usa gia'
		// il suo colore definitivo, cosi' il tratteggio non "cambia colore" al rilascio del mouse.
		const base = activeType === "gabbia" ? BASE_RECT_COLORS.new : BASE_RECT_COLORS.bannerNew;
		return {stroke: base.stroke, strokeWidth: rectStrokeWidth, fill: base.fill.replace("1)", `${rectOpacity})`), dash: [5, 5]};
	}, [activeType, rectStrokeWidth, rectOpacity]);

	// Stile del rettangolo di selezione (marquee): blu neutro, non il colore di banner/gabbia —
	// deve leggersi come "sto selezionando", non come un'anteprima di nuova gabbia/banner.
	const marqueeStyle = {stroke: "#1677ff", strokeWidth: 1, fill: "rgba(22, 119, 255, 0.08)", dash: [4, 4]};

	const hidePreviewRect = useCallback(() =>
	{
		const r = previewRectRef.current;
		if (!r) return;
		r.visible(false);
		r.getLayer()?.batchDraw();
	}, []);

	// ─── Drawing handlers (variant only) ───
	const getPointerPos = useCallback(() =>
	{
		const s = variantStageRef.current;
		if (!s) return null;
		const p = s.getPointerPosition();
		if (!p) return null;
		// Lo Stage è traslato di STAGE_MARGIN (px schermo) per centrare la pagina:
		// scalo il puntatore allo stesso sistema di coordinate della scena.
		return {x: (p.x - STAGE_MARGIN) / variantZoom, y: (p.y - STAGE_MARGIN) / variantZoom};
	}, [variantZoom]);

	const handleVMouseDown = (e) =>
	{
		if (!variant.pages.length) return;
		const pos = getPointerPos();
		if (pos) lastPointerRef.current = {...pos, pageIndex: variantPageIdx};
		const empty = e.target === variantStageRef.current || e.target.attrs?.name === "page-image";
		if (!empty) return;

		if (hasSelectionModifier(e))
		{
			// Cmd/Ctrl/Shift + trascina su area vuota → selezione multipla a rettangolo.
			// Prima qui si usciva senza fare nulla: il gesto sembrava non funzionare affatto.
			isMarqueeSelecting.current = true;
			startPos.current = getPointerPos();
			return;
		}

		setSelectedIds([]);
		isDrawing.current = true;
		startPos.current = getPointerPos();
	};

	const handleVMouseMove = () =>
	{
		const pos = getPointerPos();
		if (pos) lastPointerRef.current = {...pos, pageIndex: variantPageIdx};

		if (isMarqueeSelecting.current)
		{
			if (!pos || !startPos.current) return;
			const r = previewRectRef.current;
			if (!r) return;
			r.setAttrs({
				x: Math.min(startPos.current.x, pos.x), y: Math.min(startPos.current.y, pos.y),
				width: Math.abs(pos.x - startPos.current.x), height: Math.abs(pos.y - startPos.current.y),
				...marqueeStyle, visible: true
			});
			r.getLayer()?.batchDraw();
			return;
		}

		if (!isDrawing.current || !pos || !startPos.current) return;
		const r = previewRectRef.current;
		if (!r) return;
		r.setAttrs({
			x: Math.min(startPos.current.x, pos.x), y: Math.min(startPos.current.y, pos.y),
			width: Math.abs(pos.x - startPos.current.x), height: Math.abs(pos.y - startPos.current.y),
			...previewStyle, visible: true
		});
		r.getLayer()?.batchDraw();
	};

	const handleVMouseUp = async () =>
	{
		if (isMarqueeSelecting.current)
		{
			isMarqueeSelecting.current = false;
			hidePreviewRect();
			const end = getPointerPos();
			const start = startPos.current;
			startPos.current = null;
			if (!end || !start) return;

			const w = Math.abs(end.x - start.x), h = Math.abs(end.y - start.y);
			if (w <= 3 || h <= 3) return; // trascinamento troppo piccolo: non è un vero marquee

			// Rettangolo di selezione in coordinate pagina (identiche a zone.x/y/w/h in questo file).
			const selLeft = toFull(Math.min(start.x, end.x));
			const selTop = toFull(Math.min(start.y, end.y));
			const selRight = toFull(Math.max(start.x, end.x));
			const selBottom = toFull(Math.max(start.y, end.y));

			// Intersezione rettangolo-rettangolo (non serve contenimento totale): qualunque
			// gabbia anche solo sfiorata dal rettangolo entra in selezione.
			const hitIds = variant.zones
				.filter((z) => z.pageIndex === variantPageIdx)
				.filter((z) => z.x < selRight && z.x + z.w > selLeft && z.y < selBottom && z.y + z.h > selTop)
				.map((z) => z.id);

			if (hitIds.length)
				setSelectedIds((prev) => [...new Set([...prev, ...hitIds])]);
			return;
		}

		if (!isDrawing.current) return;
		isDrawing.current = false;
		hidePreviewRect();
		const end = getPointerPos();
		const start = startPos.current;
		startPos.current = null;
		if (!end || !start) return;
		const w = Math.abs(end.x - start.x), h = Math.abs(end.y - start.y);
		if (w > 5 && h > 5)
		{
			// Seriale prenotato atomicamente dal server (univoco a livello promo).
			const reserved = await reserveSerials(1);
			let newId;
			if (reserved.status === "ok")
				newId = reserved.serials[0];
			else if (reserved.status === "no-promo")
				newId = nextNewId();
			else
			{
				message.error({content: "Impossibile assegnare il seriale: rete non disponibile. Riprova.", key: "reserve-serial"});
				return;
			}

			// Vincola la nuova gabbia dentro i bordi pagina (nessuno sconfinamento nel margine canvas).
			const newZone = clampZoneToPage({
				id: newId, pageIndex: variantPageIdx, type: activeType, status: "new",
				x: toFull(Math.min(start.x, end.x)), y: toFull(Math.min(start.y, end.y)),
				w: toFull(w), h: toFull(h)
			}, vPageW, vPageH);
			variant.setZones(prev => [...prev, newZone]);
			setSelectedIds([newId]);
		}
	};

	// ─── Zone selection ───
	const handleZoneSelect = useCallback((zoneId, event) =>
	{
		// Stesso fix di FlyerPreprocessPage#handleZoneSelection: Konva genera un evento click/tap
		// sul rettangolo per qualsiasi pulsante del mouse, quindi un tasto destro su una gabbia
		// già in multi-selezione (senza modificatori) la collassava a quella sola gabbia prima
		// che il menu contestuale (handleZoneContextMenu) potesse operare sulla selezione intera.
		if ((event?.evt || event)?.button === 2)
			return;

		const toggle = hasSelectionModifier(event);
		setSelectedIds(prev =>
		{
			if (!toggle) return [zoneId];
			return prev.includes(zoneId) ? prev.filter(id => id !== zoneId) : [...prev, zoneId];
		});
	}, []);

	// ─── Selezione zone padre ───
	const handleParentZoneSelect = useCallback((zoneId, event) =>
	{
		const toggle = hasSelectionModifier(event);
		setParentSelectedIds(prev =>
		{
			if (!toggle) return [zoneId];
			return prev.includes(zoneId) ? prev.filter(id => id !== zoneId) : [...prev, zoneId];
		});
	}, []);

	// ─── Selezione a rettangolo sul riferimento (stessa logica del pannello variante) ───
	const getParentPointerPos = useCallback(() =>
	{
		const s = parentStageRef.current;
		if (!s) return null;
		const p = s.getPointerPosition();
		if (!p) return null;
		return {x: (p.x - STAGE_MARGIN) / parentZoom, y: (p.y - STAGE_MARGIN) / parentZoom};
	}, [parentZoom]);

	const handleParentMouseDown = useCallback((e) =>
	{
		const empty = e.target === e.target.getStage() || e.target.attrs?.name === "parent-page-image";
		if (!empty) return;

		if (hasSelectionModifier(e))
		{
			isParentMarqueeSelecting.current = true;
			parentStartPos.current = getParentPointerPos();
			return;
		}

		setParentSelectedIds([]);
	}, [getParentPointerPos]);

	const handleParentMouseMove = useCallback(() =>
	{
		if (!isParentMarqueeSelecting.current) return;
		const pos = getParentPointerPos();
		if (!pos || !parentStartPos.current) return;
		const r = parentPreviewRectRef.current;
		if (!r) return;
		r.setAttrs({
			x: Math.min(parentStartPos.current.x, pos.x), y: Math.min(parentStartPos.current.y, pos.y),
			width: Math.abs(pos.x - parentStartPos.current.x), height: Math.abs(pos.y - parentStartPos.current.y),
			...marqueeStyle, visible: true
		});
		r.getLayer()?.batchDraw();
	}, [getParentPointerPos]);

	const handleParentMouseUp = useCallback(() =>
	{
		if (!isParentMarqueeSelecting.current) return;
		isParentMarqueeSelecting.current = false;
		const r = parentPreviewRectRef.current;
		if (r)
		{
			r.visible(false);
			r.getLayer()?.batchDraw();
		}

		const end = getParentPointerPos();
		const start = parentStartPos.current;
		parentStartPos.current = null;
		if (!end || !start) return;

		const w = Math.abs(end.x - start.x), h = Math.abs(end.y - start.y);
		if (w <= 3 || h <= 3) return; // trascinamento troppo piccolo: non è un vero marquee

		const selLeft = Math.min(start.x, end.x);
		const selTop = Math.min(start.y, end.y);
		const selRight = Math.max(start.x, end.x);
		const selBottom = Math.max(start.y, end.y);

		// Intersezione rettangolo-rettangolo: qualunque gabbia anche solo sfiorata entra
		// in selezione, sommandosi a quella già presente (es. da click precedenti).
		const hitIds = parent.zones
			.filter((z) => z.pageIndex === parentPageIdx)
			.filter((z) => z.x < selRight && z.x + z.w > selLeft && z.y < selBottom && z.y + z.h > selTop)
			.map((z) => z.id);

		if (hitIds.length)
			setParentSelectedIds((prev) => [...new Set([...prev, ...hitIds])]);
	}, [getParentPointerPos, parent.zones, parentPageIdx]);

	// ─── Primo seriale libero (non presente né nel padre né nella variante) ───
	const nextNewId = useCallback(() =>
	{
		const maxParent = parent.zones.reduce((m, z) => Math.max(m, z.id), 0);
		const maxVariant = variant.zones.reduce((m, z) => Math.max(m, z.id), 0);
		return Math.max(maxParent, maxVariant) + 1;
	}, [parent.zones, variant.zones]);

	// ─── Copia zone padre → variante (mantiene seriale originale) ───
	const copyParentZonesToVariant = useCallback((zonesToCopy) =>
	{
		if (!zonesToCopy.length) return;
		if (!variant.pages.length)
		{
			message.warning("Carica il PDF della variante prima di copiare le gabbie");
			return;
		}
		const existingIds = new Set(variant.zones.map(z => z.id));
		const actualToCopy = zonesToCopy.filter(z => !existingIds.has(z.id));
		if (!actualToCopy.length)
		{
			message.warning("Gabbie già presenti nella variante");
			return;
		}
		const pW = parent.pageWidth || 1;
		const pH = parent.pageHeight || 1;
		const vW = variant.pageWidth || pW;
		const vH = variant.pageHeight || pH;
		const scaleX = vW / pW;
		const scaleY = vH / pH;
		// vW/vH sono le dimensioni pagina della variante in full-res → clamp diretto (nessun toFull),
		// così anche le gabbie copiate dal riferimento restano interamente dentro la pagina variante.
		const copied = actualToCopy.map((z) => clampZoneToPage({
			id: z.id, pageIndex: variantPageIdx, type: z.type, status: "inherited",
			// Sorgente di ereditarietà = riferimento sinistro corrente (multi-sorgente)
			sourceFlyerId: parentFlyerId,
			x: z.x * scaleX, y: z.y * scaleY, w: z.w * scaleX, h: z.h * scaleY
		}, vW, vH));
		variant.setZones(prev => [...prev, ...copied]);
		setSelectedIds(copied.map(z => z.id));
		setParentSelectedIds([]);
		message.success(`${copied.length} ${copied.length === 1 ? "gabbia copiata" : "gabbie copiate"} nella variante`);
	}, [parent.pageWidth, parent.pageHeight, variant, variantPageIdx, message, parentFlyerId]);

	const handleCopyToVariant = useCallback(() =>
	{
		const selected = parent.zones.filter(z => parentSelectedIds.includes(z.id) && z.pageIndex === parentPageIdx);
		copyParentZonesToVariant(selected);
	}, [parentSelectedIds, parentPageIdx, parent.zones, copyParentZonesToVariant]);

	// ─── Doppio click su zona padre → copia diretta nella variante ───
	const handleParentZoneDblClick = useCallback((zoneId) =>
	{
		const zone = parent.zones.find(z => z.id === zoneId);
		if (zone) copyParentZonesToVariant([zone]);
	}, [parent.zones, copyParentZonesToVariant]);

	// Seriali gia' presenti nella variante: serve sia al pulsante "Trasferisci gabbie"
	// sia al badge del canvas riferimento (spunta sui seriali gia' presi dal padre).
	const variantZoneIdSet = useMemo(() => new Set(variant.zones.map(z => z.id)), [variant.zones]);

	// ─── Trasferisci tutte le gabbie della pagina padre corrente ───
	const transferableParentZones = useMemo(
		() => parent.zones.filter(z => z.pageIndex === parentPageIdx && !variantZoneIdSet.has(z.id)),
		[parent.zones, variantZoneIdSet, parentPageIdx]
	);

	const handleTransferAll = useCallback(() =>
	{
		if (!transferableParentZones.length) return;
		copyParentZonesToVariant(transferableParentZones);
	}, [transferableParentZones, copyParentZonesToVariant]);

	// ─── Keyboard shortcuts ───
	useEffect(() =>
	{
		const onKey = (e) =>
		{
			if (isEditableTarget(e.target)) return;
			const key = (e.key || "").toLowerCase();
			if ((key === "delete" || key === "backspace") && selectedIds.length)
			{
				e.preventDefault();
				variant.setZones(prev => prev.filter(z => !selectedIdsSet.has(z.id)));
				setSelectedIds([]);
				return;
			}

			// Pressioni consecutive dello stesso tipo (frecce, oppure scala) = un solo passo di undo:
			// al primo tasto della sequenza impila lo stato pre-sequenza, poi sopprime la registrazione
			// dei passi successivi. Chiamarla solo se lo stato cambierà davvero (l'effetto dello
			// storico deve vedere un nuovo array per consumare la soppressione).
			const beginKeyboardBurst = (kind) =>
			{
				if (!arrowBurstRef.current || burstKindRef.current !== kind)
				{
					undoStackRef.current.push(prevZonesRef.current);
					if (undoStackRef.current.length > 50) undoStackRef.current.shift();
					redoStackRef.current = [];
					arrowBurstRef.current = true;
					burstKindRef.current = kind;
				}
				if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
				arrowTimerRef.current = setTimeout(() => { arrowBurstRef.current = false; }, ARROW_COALESCE_MS);
				suppressHistoryRef.current = true;
			};

			// Frecce: sposta le gabbie selezionate della variante (Shift = passo grande). Senza Ctrl/Cmd.
			// Si muovono come un blocco: al bordo pagina si ferma tutto il gruppo, invece di
			// schiacciare le gabbie una sull'altra.
			const ARROW_STEPS = {arrowup: [0, -1], arrowdown: [0, 1], arrowleft: [-1, 0], arrowright: [1, 0]};
			if (ARROW_STEPS[key] && selectedIds.length && !e.ctrlKey && !e.metaKey)
			{
				e.preventDefault();
				beginKeyboardBurst("move");

				const [dirX, dirY] = ARROW_STEPS[key];
				const stepPreview = e.shiftKey ? 10 : 1; // px pagina (stesso spazio delle coordinate gabbia)
				const dx = toFull(stepPreview) * dirX;
				const dy = toFull(stepPreview) * dirY;
				const pageW = vPageW, pageH = vPageH;
				variant.setZones(prev =>
				{
					const moves = new Map(prev
						.filter(z => selectedIdsSet.has(z.id))
						.map(z => [z.id, {x: z.x + dx, y: z.y + dy}]));
					return applyZoneGeometryUpdates(prev, moves, pageW, pageH);
				});
				return;
			}

			// Cmd/Ctrl+. e Cmd/Ctrl+, (con Option/Alt passo 5%): scala le gabbie selezionate della
			// pagina a schermo dal centro del loro riquadro comune, come in InDesign. Il browser non
			// deve reagire nemmeno senza selezione: Cmd+, aprirebbe le impostazioni di Chrome.
			const scaleFactor = getZoneScaleFactor(e);
			if (scaleFactor)
			{
				e.preventDefault();
				const pageW = vPageW, pageH = vPageH;
				const scaleTargets = (list) => list.filter(z => z.pageIndex === variantPageIdx && selectedIdsSet.has(z.id));
				if (!scaleZonesFromCenter(scaleTargets(variant.zones), scaleFactor, pageW, pageH)) return;
				beginKeyboardBurst("scale");
				variant.setZones(prev =>
				{
					const scaled = scaleZonesFromCenter(scaleTargets(prev), scaleFactor, pageW, pageH) || [];
					const byId = new Map(scaled.map(z => [z.id, z]));
					return prev.map(z => byId.get(z.id) || z);
				});
				return;
			}

			if (!(e.ctrlKey || e.metaKey)) return;

			// Annulla (Cmd/Ctrl+Z) e Rifai (Cmd/Ctrl+Shift+Z) sullo storico strutturale della variante.
			// prevZonesRef.current è lo stato corrente affidabile: lo si sposta sulla pila opposta.
			if (key === "z")
			{
				// Chiude un'eventuale sequenza di frecce, così il prossimo nudge sarà un passo a sé.
				arrowBurstRef.current = false;
				if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
				if (e.shiftKey)
				{
					if (!redoStackRef.current.length) return;
					e.preventDefault();
					restoringRef.current = true;
					undoStackRef.current.push(prevZonesRef.current);
					if (undoStackRef.current.length > 50) undoStackRef.current.shift();
					const next = redoStackRef.current.pop();
					setSelectedIds([]);
					variant.setZones(next);
					return;
				}
				if (!undoStackRef.current.length) return;
				e.preventDefault();
				restoringRef.current = true;
				redoStackRef.current.push(prevZonesRef.current);
				if (redoStackRef.current.length > 50) redoStackRef.current.shift();
				const previous = undoStackRef.current.pop();
				setSelectedIds([]);
				variant.setZones(previous);
				return;
			}

			if (key === "c" && selectedIds.length)
			{
				const copied = variant.zones.filter(z => selectedIdsSet.has(z.id));
				if (!copied.length) return;
				const ox = copied.reduce((m, z) => Math.min(m, z.x), Infinity);
				const oy = copied.reduce((m, z) => Math.min(m, z.y), Infinity);
				e.preventDefault();
				clipboardRef.current = {originX: ox, originY: oy, zones: copied.map(z => ({...z}))};
			}
			// Tasto V rilevato via e.code (fisico): su Mac con Option/Alt premuto e.key diventa un
			// carattere speciale (es. "√"), quindi non ci si può basare su key per il combo Alt.
			if ((e.code === "KeyV" || key === "v") && clipboardRef.current?.zones?.length)
			{
				const {zones: copied, originX, originY} = clipboardRef.current;
				// Cmd/Ctrl + Shift + Alt(Option) + V = incolla nella STESSA posizione dell'originale
				// (standard InDesign); qualsiasi altra combinazione con V = incolla al puntatore/centro.
				const inPlace = e.shiftKey && e.altKey;
				const ptr = lastPointerRef.current;
				const baseW = vPageW, baseH = vPageH;
				const ax = ptr?.pageIndex === variantPageIdx ? ptr.x : baseW / 2;
				const ay = ptr?.pageIndex === variantPageIdx ? ptr.y : baseH / 2;
				e.preventDefault();

				// Seriali prenotati atomicamente dal server (univoci a livello promo).
				(async () =>
				{
					const reserved = await reserveSerials(copied.length);
					let ids;
					if (reserved.status === "ok")
						ids = reserved.serials;
					else if (reserved.status === "no-promo")
					{
						const baseId = nextNewId();
						ids = copied.map((_, i) => baseId + i);
					}
					else
					{
						message.error({content: "Impossibile assegnare i seriali: rete non disponibile. Riprova.", key: "reserve-serial"});
						return;
					}

					// Ogni copia incollata viene vincolata dentro i bordi pagina (niente sconfinamenti).
					const pasted = copied.map((z, i) => clampZoneToPage({
						id: ids[i], pageIndex: variantPageIdx, type: z.type, status: "new",
						x: inPlace ? z.x : ax + (z.x - originX), y: inPlace ? z.y : ay + (z.y - originY), w: z.w, h: z.h
					}, baseW, baseH));
					variant.setZones(prev => [...prev, ...pasted]);
					setSelectedIds(pasted.map(z => z.id));
				})();
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [selectedIds, selectedIdsSet, variant, variantPageIdx, vPageW, vPageH, toFull, nextNewId, reserveSerials, message]);

	// ─── Gestione pagine della variante ───
	// Stessa logica della pre-elaborazione volantino: opera solo sulla variante, mai sul
	// riferimento. `pagesBusy` sospende l'autosalvataggio qui sotto.
	const handleAfterPageDelete = useCallback(
		() => setVariantPageIdx((prev) => Math.max(0, Math.min(prev, variant.pages.length - 2))),
		[variant.pages.length]
	);

	const {
		pagesBusy,
		pagesBusyRef,
		addPagesOpen,
		openAddPages,
		closeAddPages,
		handlePagesDragEnd,
		handlePageMenuAction,
		handleAddPagesCompleted
	} = usePageManagement({
		flyerId: variantId,
		pages: variant.pages,
		refetch: variant.refetch,
		onAfterDelete: handleAfterPageDelete
	});

	// ─── Autosave variant zones ───
	const debouncedSave = useRef(null);
	zonesRef.current = variant.zones;
	useEffect(() =>
	{
		if (variant.loading || !variantId) return;
		// Se la GET /preprocess fallisce (backend che riparte, blip di rete, token scaduto),
		// il hook mette loading=false lasciando zones=[] — e senza questa guardia l'autosave
		// scriveva quell'array vuoto, CANCELLANDO tutte le gabbie sul server (l'endpoint
		// sostituisce l'intero array). La pre-elaborazione volantino ha da sempre la stessa
		// protezione via loadedFlyerIdRef; qui mancava.
		if (variant.loadedFlyerIdRef.current !== variantId) return;
		if (variant.zones.length && !variant.pages.length) return;
		// Operazione sulle pagine in corso: il server sta riscrivendo l'intero array dei
		// riquadri rimappandone gli indici di pagina, e questo salvataggio rimetterebbe quelli
		// vecchi. Il flag è in dipendenza (il cleanup annulla il timer già in coda) ED è
		// ricontrollato allo scatto, perché un timer armato un istante prima dell'operazione
		// non passa dal cleanup.
		if (pagesBusy) return;
		if (debouncedSave.current) clearTimeout(debouncedSave.current);
		debouncedSave.current = setTimeout(async () =>
		{
			if (pagesBusyRef.current) return;
			setSaving(true);
			try
			{
				const zonesToSave = variant.zones;
				// Niente previewScale nel payload: la scala è una proprietà dei file di anteprima su
				// disco, decisa al caricamento del PDF. Riscriverla da qui rischiava di disallineare
				// il valore salvato dai file realmente presenti (scheda aperta col valore vecchio).
				const {data} = await api.patch(`/api/flyers/${variantId}/preprocess-zones`, {
					zones: zonesToSave,
					config: {rectStrokeWidth, rectOpacity}
				});

				// Il server vincola le gabbie dentro i bordi pagina e rimanda indietro la
				// geometria normalizzata: senza adottarla, una gabbia storica nata fuori pagina
				// resterebbe disegnata dov'era fino a un ricaricamento, pur essendo già corretta
				// a DB. Stessa logica di FlyerPreprocessPage: si adotta solo se nel frattempo
				// l'utente non ha toccato altro, e fuori dallo storico Annulla (è una correzione
				// del server, non un'azione dell'utente).
				const serverZones = Array.isArray(data?.zones) ? data.zones : null;
				const sentSig = structuralZonesSig(zonesToSave);
				if (serverZones
					&& structuralZonesSig(serverZones) !== sentSig
					&& structuralZonesSig(zonesRef.current) === sentSig)
				{
					restoringRef.current = true;
					variant.setZones(serverZones);
				}
			}
			catch
			{ /* silent */
			}
			setTimeout(() => setSaving(false), 300);
		}, 500);
		return () =>
		{
			if (debouncedSave.current) clearTimeout(debouncedSave.current);
		};
	}, [variant.zones, variant.pages.length, variant.loading, variantId, rectStrokeWidth, rectOpacity, pagesBusy, pagesBusyRef]);

	// Nuova variante: azzera lo storico Annulla/Rifai (la pagina non fa remount al cambio di :variantId).
	useEffect(() =>
	{
		undoStackRef.current = [];
		redoStackRef.current = [];
		prevZonesRef.current = [];
		suppressHistoryRef.current = false;
		arrowBurstRef.current = false;
		if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
	}, [variantId]);

	// Pulizia del timer di coalescing frecce allo smontaggio.
	useEffect(() => () => { if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current); }, []);

	// Registra lo storico per Annulla/Rifai: impila lo stato PRECEDENTE ad ogni cambio strutturale
	// delle zone della variante. Ignora il caricamento e i ripristini undo/redo (restoringRef).
	useEffect(() =>
	{
		if (variant.loading)
		{
			prevZonesRef.current = variant.zones;
			return;
		}
		if (restoringRef.current)
		{
			restoringRef.current = false;
			prevZonesRef.current = variant.zones;
			return;
		}
		// Movimento intermedio di una sequenza di frecce: avanza la baseline SENZA registrare
		// (il passo di undo è già stato impilato all'inizio della sequenza).
		if (suppressHistoryRef.current)
		{
			suppressHistoryRef.current = false;
			prevZonesRef.current = variant.zones;
			return;
		}
		if (structuralZonesSig(prevZonesRef.current) !== structuralZonesSig(variant.zones))
		{
			undoStackRef.current.push(prevZonesRef.current);
			if (undoStackRef.current.length > 50)
				undoStackRef.current.shift();
			// Nuova azione dell'utente → svuota la pila redo (i "futuri" annullati non sono più validi).
			redoStackRef.current = [];
			// Un'azione non-freccia interrompe l'eventuale sequenza di frecce in corso.
			arrowBurstRef.current = false;
			if (arrowTimerRef.current) clearTimeout(arrowTimerRef.current);
		}
		prevZonesRef.current = variant.zones;
	}, [variant.zones, variant.loading]);

	// ─── SSE ───
	const [aiProcessing, setAiProcessing] = useState(false);
	const [ocrProgress, setOcrProgress] = useState(0);

	useSSE(variantId ? `flyer:${variantId}` : null, {
		"flyer.variant.start": () =>
		{
			setAiProcessing(true);
			setOcrProgress(5);
		},
		"flyer.status.changed": (p) =>
		{
			if (p?.status === "PROCESSING" && !p?.isProcessingStale)
			{
				setAiProcessing(true);
				setOcrProgress(p?.processingProgress || 5);
				return;
			}
			setAiProcessing(false);
			if (["ERROR", "READY", "NEW"].includes(p?.status)) setOcrProgress(0);
		},
		"flyer.ocr.progress": (p) => setOcrProgress(p?.percent || 0),
		"flyer.done": () =>
		{
			setAiProcessing(false);
			setOcrProgress(100);
			// Le card sono state riscritte: i testi prodotto in memoria per la ricerca sono
			// vecchi. Si invalida qui e non su ogni "flyer.card.created" (uno per gabbia).
			setCardTextBySerial(null);
			setTimeout(() => setOcrProgress(0), 3000);
		},
		"flyer.error": () =>
		{
			setAiProcessing(false);
			setOcrProgress(0);
		},
		// Un campo manuale è stato cambiato altrove (editor card / selezione massiva in
		// FlyerManagePage): tiene il tag sulla gabbia coerente senza richiedere un refresh.
		"flyer.zone.manualOverrides.updated": (p) =>
		{
			if (p?.seriale == null)
				return;
			variant.setZones(prev => prev.map(z => z.id === p.seriale
				? {...z, manualOverrides: p.manualOverrides, manualLabels: p.manualLabels}
				: z));
		},
		"flyer.card.error": (p) =>
		{
			if (p?.seriale != null)
			{
				variant.setZones(prev => prev.map(z => z.id === p.seriale ? {...z, status: "error"} : z));
			}
		}
	});

	// Come FlyerPreprocessPage#handleAiProcessing: {forceReprocess, serials} opzionali per
	// rielaborare solo un sottoinsieme di gabbie invece dell'intera variante. Prima mancava
	// del tutto la possibilità di passare `serials`, quindi il menu contestuale (tasto destro
	// su una gabbia/selezione) non aveva modo di offrire "Rielabora con AI" — vedi il wiring
	// di onReprocess più sotto su <ZoneContextMenu>.
	const handleAiProcessing = async ({forceReprocess = false, serials = null} = {}) =>
	{
		if (!variantId || !variant.zones.length || aiProcessing) return;
		if (!variant.pages.length)
		{
			message.warning("Pagine variante non disponibili: ricarica il PDF prima di avviare l'elaborazione AI");
			return;
		}

		// Limita i serials alle sole gabbie valide: i banner non richiedono AI.
		const gabbiaIds = new Set(variant.zones.filter(z => z.type === "gabbia").map(z => z.id));
		const cleanSerials = Array.isArray(serials)
			? [...new Set(serials)].filter(id => gabbiaIds.has(id))
			: null;
		if (Array.isArray(serials) && cleanSerials.length === 0)
		{
			message.warning("Nessuna gabbia valida selezionata");
			return;
		}

		setAiProcessing(true);
		setOcrProgress(0);
		try
		{
			const payload = {flyerId: variantId, forceReprocess};
			if (cleanSerials) payload.serials = cleanSerials;
			await api.post("/api/ai/processing-preprocess", payload);
			message.success(forceReprocess ? "Rielaborazione AI avviata" : "Elaborazione AI avviata");
		}
		catch (err)
		{
			message.error(err?.response?.data?.error || "Errore AI");
			setAiProcessing(false);
			setOcrProgress(0);
		}
	};

	// ─── Sidebar pagine inline ───
	const [parentSidebarOpen, setParentSidebarOpen] = useState(false);
	const [variantSidebarOpen, setVariantSidebarOpen] = useState(false);
	const apiBaseUrl = import.meta.env.VITE_API_URL || "";

	// ─── Conteggi gabbie ───
	const parentZoneCount = parent.zones.length;
	const variantZoneCount = variant.zones.length;
	const parentZonesByPage = useMemo(() =>
	{
		const m = new Map();
		parent.zones.forEach(z => m.set(z.pageIndex, (m.get(z.pageIndex) || 0) + 1));
		return m;
	}, [parent.zones]);
	const variantZonesByPage = useMemo(() =>
	{
		const m = new Map();
		variant.zones.forEach(z => m.set(z.pageIndex, (m.get(z.pageIndex) || 0) + 1));
		return m;
	}, [variant.zones]);

	// ─── Raggruppamento gabbie variante per pagina (come single view) ───
	const variantGroupedZones = useMemo(() =>
	{
		const groups = variant.pages.map((page, idx) => ({pageIndex: idx, page, zones: []}));
		variant.zones.forEach(z =>
		{
			if (groups[z.pageIndex]) groups[z.pageIndex].zones.push(z);
		});
		return groups.filter(g => g.zones.length > 0);
	}, [variant.pages, variant.zones]);

	// ─── Raggruppamento gabbie riferimento per pagina (stessa logica della variante) ───
	const parentGroupedZones = useMemo(() =>
	{
		const groups = parent.pages.map((page, idx) => ({pageIndex: idx, page, zones: []}));
		parent.zones.forEach(z =>
		{
			if (groups[z.pageIndex]) groups[z.pageIndex].zones.push(z);
		});
		return groups.filter(g => g.zones.length > 0);
	}, [parent.pages, parent.zones]);

	const loadCardTexts = useCallback(async () =>
	{
		if (cardTextBySerial || cardTextFetchRef.current || !variantId) return;
		cardTextFetchRef.current = true;
		setCardTextLoading(true);
		try
		{
			const {data} = await api.get(`/api/flyers/${variantId}/cards`);
			const map = new Map();
			for (const card of (Array.isArray(data) ? data : []))
			{
				if (card?.seriale == null) continue;
				const label = String(card.ai?.descrizione || card.ai?.nome_prodotto || "").trim();
				const haystack = [label, card.ai?.marca, card.ai?.nome_prodotto]
					.filter(Boolean).join(" ").toLowerCase();
				map.set(Number(card.seriale), {label, haystack});
			}
			setCardTextBySerial(map);
		}
		catch
		{
			setCardTextBySerial(new Map());
		}
		finally
		{
			cardTextFetchRef.current = false;
			setCardTextLoading(false);
		}
	}, [cardTextBySerial, variantId]);

	// La pagina non fa remount al cambio di :variantId: senza questo la cache mostrerebbe
	// le descrizioni della variante precedente accanto ai seriali di quella nuova.
	useEffect(() =>
	{
		cardTextFetchRef.current = false;
		setCardTextBySerial(null);
		setZoneQuery("");
	}, [variantId]);

	useEffect(() =>
	{
		if (zoneQuery.trim()) loadCardTexts();
	}, [zoneQuery, loadCardTexts]);

	const loadParentCardTexts = useCallback(async () =>
	{
		if (parentCardTextBySerial || parentCardTextFetchRef.current || !parentFlyerId) return;
		parentCardTextFetchRef.current = true;
		setParentCardTextLoading(true);
		try
		{
			const {data} = await api.get(`/api/flyers/${parentFlyerId}/cards`);
			const map = new Map();
			for (const card of (Array.isArray(data) ? data : []))
			{
				if (card?.seriale == null) continue;
				const label = String(card.ai?.descrizione || card.ai?.nome_prodotto || "").trim();
				const haystack = [label, card.ai?.marca, card.ai?.nome_prodotto]
					.filter(Boolean).join(" ").toLowerCase();
				map.set(Number(card.seriale), {label, haystack});
			}
			setParentCardTextBySerial(map);
		}
		catch
		{
			setParentCardTextBySerial(new Map());
		}
		finally
		{
			parentCardTextFetchRef.current = false;
			setParentCardTextLoading(false);
		}
	}, [parentCardTextBySerial, parentFlyerId]);

	// Il riferimento cambia quando l'utente sceglie un'altra opzione dal selettore
	// "Riferimento": la cache dei testi prodotto va invalidata anche in quel caso,
	// non solo al cambio di :variantId.
	useEffect(() =>
	{
		parentCardTextFetchRef.current = false;
		setParentCardTextBySerial(null);
		setParentZoneQuery("");
	}, [parentFlyerId]);

	useEffect(() =>
	{
		if (parentZoneQuery.trim()) loadParentCardTexts();
	}, [parentZoneQuery, loadParentCardTexts]);

	const zoneSearchResults = useMemo(() =>
	{
		const query = zoneQuery.trim().toLowerCase();
		if (!query) return null;
		const bareNumber = query.replace(/^#/, "");
		return variant.zones
			.filter((zone) =>
			{
				if (bareNumber && String(zone.id).includes(bareNumber)) return true;
				if (cardTextBySerial?.get(zone.id)?.haystack.includes(query)) return true;
				const labels = zone.manualLabels && typeof zone.manualLabels === "object"
					? Object.values(zone.manualLabels)
					: [];
				return labels.some((value) => String(Array.isArray(value) ? value.join(" ") : (value ?? ""))
					.toLowerCase().includes(query));
			})
			.sort((a, b) => a.pageIndex - b.pageIndex || a.id - b.id);
	}, [zoneQuery, variant.zones, cardTextBySerial]);

	const parentZoneSearchResults = useMemo(() =>
	{
		const query = parentZoneQuery.trim().toLowerCase();
		if (!query) return null;
		const bareNumber = query.replace(/^#/, "");
		return parent.zones
			.filter((zone) =>
			{
				if (bareNumber && String(zone.id).includes(bareNumber)) return true;
				if (parentCardTextBySerial?.get(zone.id)?.haystack.includes(query)) return true;
				const labels = zone.manualLabels && typeof zone.manualLabels === "object"
					? Object.values(zone.manualLabels)
					: [];
				return labels.some((value) => String(Array.isArray(value) ? value.join(" ") : (value ?? ""))
					.toLowerCase().includes(query));
			})
			.sort((a, b) => a.pageIndex - b.pageIndex || a.id - b.id);
	}, [parentZoneQuery, parent.zones, parentCardTextBySerial]);

	// Gabbie trovate dalle due ricerche, evidenziate sui rispettivi canvas (null = nessuna ricerca).
	const searchMatchIds = useMemo(
		() => (zoneSearchResults ? new Set(zoneSearchResults.map(z => z.id)) : null),
		[zoneSearchResults]
	);
	const parentSearchMatchIds = useMemo(
		() => (parentZoneSearchResults ? new Set(parentZoneSearchResults.map(z => z.id)) : null),
		[parentZoneSearchResults]
	);

	// La vista segue il primo risultato di ciascuna ricerca: cambia pagina se serve e poi scrolla
	// sulla gabbia, altrimenti l'evidenziazione potrebbe cadere su una pagina non guardata o fuori
	// dalla parte visibile. Lo scroll parte a immagine caricata (pending*FocusRef + tick, perché
	// a pagina invariata nessuna dipendenza dell'effetto di scroll cambierebbe). Scatta solo al
	// cambio dei risultati, così sfogliare a mano durante una ricerca resta libero.
	const pendingVariantFocusRef = useRef(null);
	const pendingParentFocusRef = useRef(null);
	const [searchFocusTick, setSearchFocusTick] = useState(0);

	useEffect(() =>
	{
		if (!zoneSearchResults?.length)
		{
			pendingVariantFocusRef.current = null;
			return;
		}
		const target = zoneSearchResults.find(z => z.pageIndex === variantPageIdx) || zoneSearchResults[0];
		if (target.pageIndex !== variantPageIdx) changeVariantPage(target.pageIndex);
		pendingVariantFocusRef.current = target.id;
		setSearchFocusTick(t => t + 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zoneSearchResults]);

	useEffect(() =>
	{
		if (!parentZoneSearchResults?.length)
		{
			pendingParentFocusRef.current = null;
			return;
		}
		const target = parentZoneSearchResults.find(z => z.pageIndex === parentPageIdx) || parentZoneSearchResults[0];
		if (target.pageIndex !== parentPageIdx) changeParentPage(target.pageIndex);
		pendingParentFocusRef.current = target.id;
		setSearchFocusTick(t => t + 1);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [parentZoneSearchResults]);

	useEffect(() =>
	{
		const variantId = pendingVariantFocusRef.current;
		const parentId = pendingParentFocusRef.current;
		if (variantId === null && parentId === null) return;

		const frameId = requestAnimationFrame(() =>
		{
			const vZone = variantId === null ? null : variant.zones.find(z => z.id === variantId);
			if (variantId !== null && !vZone) pendingVariantFocusRef.current = null;
			else if (vZone && vZone.pageIndex === variantPageIdx && variantImage
				&& scrollZoneIntoContainer(variantScrollRef.current, variantStageRef.current?.container?.(), vZone, variantZoom, STAGE_MARGIN))
				pendingVariantFocusRef.current = null;

			const pZone = parentId === null ? null : parent.zones.find(z => z.id === parentId);
			if (parentId !== null && !pZone) pendingParentFocusRef.current = null;
			else if (pZone && pZone.pageIndex === parentPageIdx && parentImage
				&& scrollZoneIntoContainer(parentScrollRef.current, parentStageRef.current?.container?.(), pZone, parentZoom, STAGE_MARGIN))
				pendingParentFocusRef.current = null;
		});
		return () => cancelAnimationFrame(frameId);
	}, [searchFocusTick, variantPageIdx, parentPageIdx, variantImage, parentImage, variantZoom, parentZoom, variant.zones, parent.zones]);

	const activeZonePanelKey = useMemo(() =>
	{
		if (!variantGroupedZones.length) return undefined;
		const key = String(variantPageIdx);
		// Nessun fallback al primo gruppo: se la pagina corrente non ha ancora gabbie, il pannello
		// resta chiuso invece di mostrare quello di un'altra pagina (stesso fix di
		// FlyerPreprocessPage.jsx — il fallback causava uno scatto visibile nella sidebar alla
		// prima gabbia disegnata/incollata sulla pagina corrente).
		return variantGroupedZones.some(g => String(g.pageIndex) === key) ? key : undefined;
	}, [variantPageIdx, variantGroupedZones]);

	const handlePageDragEnd = useCallback((pageIndex, event) =>
	{
		const {active, over} = event;
		if (!over || active.id === over.id) return;
		variant.setZones(prev => reorderZonesWithinPage(prev, pageIndex, active.id, over.id));
	}, [variant]);

	const handleZonePanelChange = useCallback((key) =>
	{
		const k = Array.isArray(key) ? key[0] : key;
		if (typeof k === "string") changeVariantPage(Number(k));
	}, [changeVariantPage]);

	const activeParentZonePanelKey = useMemo(() =>
	{
		if (!parentGroupedZones.length) return undefined;
		const key = String(parentPageIdx);
		return parentGroupedZones.some(g => String(g.pageIndex) === key) ? key : undefined;
	}, [parentPageIdx, parentGroupedZones]);

	const handleParentZonePanelChange = useCallback((key) =>
	{
		const k = Array.isArray(key) ? key[0] : key;
		if (typeof k === "string") changeParentPage(Number(k));
	}, [changeParentPage]);

	// ─── Modalita' di elaborazione ───
	const aiDisabled = aiProcessing || variant.zones.length === 0 || variant.pages.length === 0;
	const pageGabbie = variant.zones.filter(z => z.type === "gabbia" && z.pageIndex === variantPageIdx);

	// Rielaborazione forzata: sovrascrive anche le card gia' complete/convalidate (e riporta
	// le ereditate a una nuova estrazione AI), quindi passa sempre da una conferma esplicita.
	// Il click diretto sul pulsante elabora invece le sole gabbie nuove e non distrugge nulla.
	const confirmForcedReprocess = ({title, content, serials = null}) => modal.confirm({
		title,
		content,
		okText: "Rielabora",
		okButtonProps: {danger: true},
		cancelText: "Annulla",
		onOk: () => handleAiProcessing(serials ? {serials, forceReprocess: true} : {forceReprocess: true})
	});

	const aiMenuItems = [
		{key: "only-new", icon: <CloudUploadOutlined/>, label: "Solo le gabbie nuove", disabled: aiDisabled},
		{type: "divider"},
		{
			key: "reprocess-page",
			icon: <RedoOutlined/>,
			label: `Rielabora pagina ${variantPageIdx + 1} (${pageGabbie.length})`,
			disabled: aiDisabled || pageGabbie.length === 0
		},
		{key: "reprocess", icon: <RedoOutlined/>, danger: true, label: "Rielabora tutta la variante", disabled: aiDisabled}
	];

	const onAiMenuClick = ({key}) =>
	{
		if (key === "only-new")
			handleAiProcessing();
		else if (key === "reprocess-page")
			confirmForcedReprocess({
				title: `Rielaborare tutte le gabbie di pagina ${variantPageIdx + 1}?`,
				content: "Le card già elaborate di questa pagina, comprese quelle convalidate e quelle ereditate dal riferimento, verranno riestratte dall'AI e sovrascritte.",
				serials: pageGabbie.map(z => z.id)
			});
		else if (key === "reprocess")
			confirmForcedReprocess({
				title: "Rielaborare tutta la variante?",
				content: "Tutte le card già elaborate, comprese quelle convalidate e quelle ereditate dal riferimento, verranno riestratte dall'AI e sovrascritte."
			});
	};

	// ─── Loading ───
	if (!parentFlyerId || parent.loading || variant.loading)
	{
		return <div className="vpp-page">
			<div className="vpp-loading"><Spin size="large" tip="Caricamento..." fullscreen/></div>
		</div>;
	}

	// ═══════════════════════════════════════════════════════════
	// RENDER
	// ═══════════════════════════════════════════════════════════
	return (
		<div className="vpp-page">
			{/* ─── Header ─── */}
			<div className="vpp-header">
				<Space>
					<Button icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)} size="small">Indietro</Button>
					<Title level={4} style={{margin: 0}}>Pre-elaborazione Variante</Title>
					{variantPdfPageSize && (
						<Tooltip title="Formato pagina del PDF caricato sulla variante">
							<Text type="secondary" style={{fontSize: 12, whiteSpace: "nowrap"}}>{variantPdfPageSize}</Text>
						</Tooltip>
					)}
					{saving && <Spin size="small" style={{marginLeft: 8}}/>}
				</Space>

				<Space size="small" className="vpp-header-sync">
					{/* Il contatore vive in uno slot a larghezza fissa e l'etichetta non cambia mai:
					    prima spariva al trasferimento, il pulsante si accorciava e — con la testata
					    space-between — tutto il gruppo centrale scattava di lato. */}
					<Tooltip title="Trasferisci tutte le gabbie della pagina padre corrente nella variante">
						<Button
							size="small" icon={<ImportOutlined/>}
							onClick={handleTransferAll}
							disabled={transferableParentZones.length === 0}
						>
							Trasferisci gabbie
							<span className="vpp-btn-count">({transferableParentZones.length})</span>
						</Button>
					</Tooltip>
					<Space.Compact size="small">
						<Tooltip title="Pagina precedente">
							<Button icon={<UpOutlined/>} onClick={handleSyncPageUp}
									disabled={syncPages
										? variantPageIdx === 0
										: (parentPageIdx === 0 && variantPageIdx === 0)}
							/>
						</Tooltip>
						<Tooltip title={syncPages ? "Sincronizzazione attiva" : "Sincronizzazione disattiva"}>
							<Button
								type={syncPages ? "primary" : "default"}
								icon={syncPages ? <LinkOutlined/> : <DisconnectOutlined/>}
								onClick={() => setSyncPages(s => !s)}
							/>
						</Tooltip>
						<Tooltip title="Pagina successiva">
							<Button icon={<DownOutlined/>} onClick={handleSyncPageDown}
									disabled={syncPages
										? variantPageIdx >= variant.pages.length - 1
										: (variantPageIdx >= variant.pages.length - 1 && parentPageIdx >= parent.pages.length - 1)}
							/>
						</Tooltip>
					</Space.Compact>
					<Tooltip title="Copia gabbie selezionate dal padre nella variante">
						<Button
							size="small" icon={<CopyOutlined/>}
							onClick={handleCopyToVariant}
							disabled={parentSelectedIds.length === 0}
							type={parentSelectedIds.length > 0 ? "primary" : "default"}
						>
							Copia → Variante
							<span className="vpp-btn-count">({parentSelectedIds.length})</span>
						</Button>
					</Tooltip>
				</Space>

				<Space size="small">
					{/* Pallino colorato coerente coi colori "nuovo" usati sul canvas (viola gabbia, ciano
					    banner): la stessa resa gia' adottata in FlyerPreprocessPage, qui prima assente —
					    il controllo si perdeva fra le tante icone della testata. */}
					<Radio.Group value={activeType} onChange={e => setActiveType(e.target.value)} buttonStyle="solid" size="small">
						<Radio.Button value="banner"><span className="vpp-type-option"><span className="vpp-type-dot vpp-type-dot--banner"/>Banner</span></Radio.Button>
						<Radio.Button value="gabbia"><span className="vpp-type-option"><span className="vpp-type-dot vpp-type-dot--gabbia"/>Gabbia</span></Radio.Button>
					</Radio.Group>
					<Popover
						trigger="click"
						placement="bottomRight"
						content={(
							<div className="vpp-style-pop">
								<div className="vpp-style-row">
									<Text type="secondary">Spessore bordo</Text>
									<InputNumber min={1} max={10} value={rectStrokeWidth} onChange={v => setRectStrokeWidth(v || 1)} size="small" style={{width: 80}}/>
								</div>
								<div className="vpp-style-row">
									<Text type="secondary">Opacita ({Math.round(rectOpacity * 100)}%)</Text>
									<Slider min={0} max={1} step={0.05} value={rectOpacity} onChange={v => setRectOpacity(v)} tooltip={{open: false}} style={{width: 150}}/>
								</div>
								<div className="vpp-legend">
									{ZONE_LEGEND.map(item => (
										<div key={item.key} className="vpp-legend-row">
											<span className="vpp-legend-dot" style={{backgroundColor: BASE_RECT_COLORS[item.key].stroke}}/>
											<Text type="secondary">{item.label}</Text>
										</div>
									))}
								</div>
							</div>
						)}
					>
						<Tooltip title="Stile gabbie e legenda colori">
							<Button size="small" icon={<BgColorsOutlined/>}>Stile</Button>
						</Tooltip>
					</Popover>
					<Tooltip title={showSerials ? "Nascondi i seriali sulle gabbie" : "Mostra i seriali sulle gabbie"}>
						<Button
							size="small" icon={<NumberOutlined/>}
							type={showSerials ? "primary" : "default"}
							onClick={() => setShowSerials(v => !v)}
						/>
					</Tooltip>
					<Tooltip title="Adatta zoom a entrambi i canvas">
						<Button size="small" icon={<ExpandOutlined/>} onClick={handleFitBoth}/>
					</Tooltip>
					<ShortcutsLegend variant size="small"/>
					{/* Split button: il click diretto elabora le SOLE gabbie nuove (azione non
					    distruttiva), la freccia espone le rielaborazioni forzate — le stesse
					    modalita' della pre-elaborazione volantino. */}
					<Space.Compact size="small">
						<Button type="primary" icon={<CloudUploadOutlined/>}
								onClick={() => handleAiProcessing()}
								loading={aiProcessing}
								style={aiProcessing && ocrProgress > 0 ? {background: `linear-gradient(90deg, #ff9800 ${ocrProgress}%, #f0f0f0 ${ocrProgress}%)`, borderColor: "#ff9800"} : {}}
								disabled={aiDisabled}
								size="small"
						>
							{aiProcessing ? `${ocrProgress}%` : "Elaborazione AI"}
						</Button>
						<Dropdown trigger={["click"]} menu={{items: aiMenuItems, onClick: onAiMenuClick}} disabled={aiDisabled}>
							<Tooltip title="Scegli cosa elaborare">
								<Button type="primary" size="small" icon={<DownOutlined/>} disabled={aiDisabled}/>
							</Tooltip>
						</Dropdown>
					</Space.Compact>
					<Dropdown
						trigger={["click"]}
						menu={{
							items: [{key: "reloadpdf", icon: <CloudUploadOutlined/>, label: "Ricarica PDF"}],
							onClick: ({key}) => { if (key === "reloadpdf") setPdfModalOpen(true); }
						}}
					>
						<Button size="small">Azioni <DownOutlined/></Button>
					</Dropdown>
					<Button size="small" onClick={() => navigate(`/flyermanage/${variantId}`)}>Gestione</Button>
				</Space>
			</div>

			{/* ─── Split area ─── */}
			<div className="vpp-split" ref={splitContainerRef}>
				{/* ═══ LATO PADRE (sola lettura) ═══ */}
				<div className="vpp-pane" style={{flex: `0 0 ${splitRatio * 100}%`}}>
					<div className="vpp-pane-header vpp-pane-header-left">
						<Space size={6}>
							<Button size="small" icon={<InsertRowLeftOutlined/>} onClick={() => setParentSidebarOpen(s => !s)} type={parentSidebarOpen ? "primary" : "default"}>Riferimento<span className="vpp-btn-count">({parentZoneCount})</span></Button>
							{referenceOptions.length > 0 ? (
								<Select
									size="small"
									style={{minWidth: 200}}
									value={parentFlyerId ? String(parentFlyerId) : undefined}
									onChange={(val) => setParentFlyerId(val)}
									options={referenceOptions}
									placeholder="Riferimento"
									showSearch
									filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
								/>
							) : (
								<Text className="vpp-pane-label">Riferimento: {variantFlyer?.parentFlyerName || "Volantino"}</Text>
							)}
						</Space>
						<Space size={4}>
							<Button size="small" disabled={parentPageIdx <= 0} onClick={() => changeParentPage(parentPageIdx - 1)}><LeftOutlined/></Button>
							<PageJumper current={parentPageIdx} total={parent.pages.length} onGo={changeParentPage}>
								<Text style={{fontSize: 12, minWidth: 50, textAlign: "center"}}>{parent.pages.length ? `${parentPageIdx + 1} / ${parent.pages.length}` : "—"}</Text>
							</PageJumper>
							<Button size="small" disabled={parentPageIdx >= parent.pages.length - 1} onClick={() => changeParentPage(parentPageIdx + 1)}><RightOutlined/></Button>
							<Tooltip title="Zoom Out"><Button size="small" icon={<ZoomOutOutlined/>} onClick={() => setParentZoom(z => Math.max(0.1, +(z - 0.1).toFixed(1)))}/></Tooltip>
							<Text style={{fontSize: 11, minWidth: 32, textAlign: "center"}}>{Math.round(parentZoom * 100)}%</Text>
							<Tooltip title="Zoom In"><Button size="small" icon={<ZoomInOutlined/>} onClick={() => setParentZoom(z => Math.min(2, +(z + 0.1).toFixed(1)))}/></Tooltip>
						</Space>
					</div>
					<div className="vpp-pane-body">
						{parentSidebarOpen && (
							<div className="vpp-pages-sidebar">
								{/* Riferimento in sola lettura: è un altro volantino, spesso il padre di più
								    varianti, e riordinarlo da qui avrebbe effetti su schermate che l'utente
								    non sta guardando. */}
								<Text strong style={{fontSize: 13, padding: "8px 0"}}>Pagine</Text>
								<div className="vpp-pages-list">
									{parent.pages.map((page, idx) =>
									{
										const rawUrl = page.previewUrl || page.url;
										const src = rawUrl ? (rawUrl.startsWith("http") ? rawUrl : `${apiBaseUrl}${rawUrl}`) : "";
										return (
											<PageThumb
												key={page.name || idx}
												index={idx}
												isActive={idx === parentPageIdx}
												zoneCount={parentZonesByPage.get(idx) || 0}
												thumbSrc={src}
												onSelect={changeParentPage}
											/>
										);
									})}
								</div>
							</div>
						)}
						{/* ─── Sidebar gabbie riferimento (sola lettura) ─── */}
						<div className="vpp-zones-sidebar">
							<Input
								className="vpp-zones-search"
								size="small"
								allowClear
								prefix={<SearchOutlined/>}
								placeholder="Cerca seriale o prodotto…"
								value={parentZoneQuery}
								onFocus={loadParentCardTexts}
								onChange={(e) => setParentZoneQuery(e.target.value)}
							/>
							<div className="vpp-zones-list">
								{parentZoneSearchResults ? (
									parentZoneSearchResults.length ? (
										<>
											<Text type="secondary" className="vpp-zones-search-count">
												{parentZoneSearchResults.length} {parentZoneSearchResults.length === 1 ? "risultato" : "risultati"} nel riferimento
												{parentCardTextLoading ? " · carico…" : ""}
											</Text>
											{parentZoneSearchResults.map((zone) => (
												<ZoneResultItem
													key={zone.id}
													zone={zone}
													isSelected={parentSelectedIdsSet.has(zone.id)}
													description={parentCardTextBySerial?.get(zone.id)?.label}
													isVariantContext={parentIsVariant}
													onSelect={(event) =>
													{
														// Come nella variante: un risultato può stare su un'altra
														// pagina, selezionarlo e basta lascerebbe la gabbia fuori schermo.
														if (zone.pageIndex !== parentPageIdx) changeParentPage(zone.pageIndex);
														handleParentZoneSelect(zone.id, event);
													}}
												/>
											))}
										</>
									) : (
										<Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
											description={parentCardTextLoading ? "Carico…" : "Nessuna gabbia trovata"}
											style={{marginTop: 24}}/>
									)
								) : parentGroupedZones.length ? (
									<Collapse
										accordion
										activeKey={activeParentZonePanelKey}
										onChange={handleParentZonePanelChange}
										className="vpp-zones-collapse"
										items={parentGroupedZones.map((group) => ({
											key: String(group.pageIndex),
											label: (
												<div style={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
													<Text strong>P{group.pageIndex + 1} ({group.zones.length})</Text>
												</div>
											),
											children: (
												<div style={{display: "flex", flexDirection: "column", gap: 6}}>
													{group.zones.map((z) => (
														<ZoneResultItem
															key={z.id}
															zone={z}
															isSelected={parentSelectedIdsSet.has(z.id)}
															description={parentCardTextBySerial?.get(z.id)?.label}
															isVariantContext={parentIsVariant}
															onSelect={(event) => handleParentZoneSelect(z.id, event)}
														/>
													))}
												</div>
											)
										}))}
									/>
								) : (
									<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessuna gabbia" style={{marginTop: 24}}/>
								)}
							</div>
						</div>
						<div ref={parentScrollRef} className="vpp-canvas-area readonly">
							<div className="vpp-canvas-wrapper">
								{parentPage && <Text className="vpp-page-label">Padre - Pagina {parentPageIdx + 1}</Text>}
								<Stage ref={parentStageRef} width={pStageW} height={pStageH} x={STAGE_MARGIN} y={STAGE_MARGIN} scaleX={parentZoom} scaleY={parentZoom}
									   onMouseDown={handleParentMouseDown}
									   onMouseMove={handleParentMouseMove}
									   onMouseUp={handleParentMouseUp}
								>
									<Layer listening={false}>
										{parentImage && (
											<KonvaImage name="parent-page-image" image={parentImage} width={pPageW} height={pPageH} perfectDrawEnabled={false}/>
										)}
									</Layer>
									<Layer>
										<Rect ref={parentPreviewRectRef} visible={false} listening={false}/>
										{parentImage && parent.zones.filter(z => z.pageIndex === parentPageIdx).map((zone) =>
										{
											// Stessa scala colori del canvas variante: quando il riferimento è a sua
											// volta una variante, le sue gabbie ereditate e quelle aggiunte a mano
											// devono distinguersi — prima erano tutte blu perché il colore dipendeva
											// dal solo tipo.
											const base = getZoneColor(zone, parentIsVariant);
											const isParentSelected = parentSelectedIdsSet.has(zone.id);
											return (
												<React.Fragment key={zone.id}>
												<Rect
													x={pToPreview(zone.x)} y={pToPreview(zone.y)}
													width={pToPreview(zone.w)} height={pToPreview(zone.h)}
													// La selezione NON cambia più il colore (era arancio fisso, ormai
													// identico all'ereditata): resta il colore dello stato e la selezione
													// si legge da bordo più spesso, alone e riempimento più carico —
													// come già fa il canvas variante.
													stroke={base.stroke}
													strokeWidth={isParentSelected ? rectStrokeWidth + 1 : rectStrokeWidth}
													fill={base.fill.replace("1)", `${isParentSelected ? Math.min(1, rectOpacity + 0.25) : rectOpacity})`)}
													shadowColor={isParentSelected ? base.stroke : undefined}
													shadowBlur={isParentSelected ? 10 : 0}
													shadowOpacity={isParentSelected ? 0.6 : 0}
													{...zoneSearchHighlightProps(getZoneSearchState(parentSearchMatchIds, zone.id), parentZoom)}
													hitStrokeWidth={10}
													onClick={(e) => handleParentZoneSelect(zone.id, e)}
													onTap={(e) => handleParentZoneSelect(zone.id, e)}
													onDblClick={() => handleParentZoneDblClick(zone.id)}
													onDblTap={() => handleParentZoneDblClick(zone.id)}
												/>
												{showSerials && (
													<ZoneSerialBadge
														x={pToPreview(zone.x)} y={pToPreview(zone.y)} w={pToPreview(zone.w)}
														seriale={zone.id}
														color={base.stroke}
														taken={variantZoneIdSet.has(zone.id)}
														dimmed={getZoneSearchState(parentSearchMatchIds, zone.id) === "dim"}
													/>
												)}
												</React.Fragment>
											);
										})}
									</Layer>
								</Stage>
							</div>
						</div>
					</div>
					{/* chiude vpp-pane-body */}
				</div>

				{/* ═══ DIVISORE RIDIMENSIONABILE ═══ */}
				<div className={`vpp-divider ${isDraggingDivider.current ? "active" : ""}`} onMouseDown={handleDividerMouseDown}/>

				{/* ═══ LATO VARIANTE (editabile) ═══ */}
				<div className="vpp-pane" style={{flex: 1}}>
					<div className="vpp-pane-header">
						<Text className="vpp-pane-label">Variante: {variantFlyer?.name || "Volantino"}</Text>
						<Space size={4}>
							<Button size="small" disabled={variantPageIdx <= 0} onClick={() => changeVariantPage(variantPageIdx - 1)}><LeftOutlined/></Button>
							<PageJumper current={variantPageIdx} total={variant.pages.length} onGo={changeVariantPage}>
								<Text style={{fontSize: 12, minWidth: 50, textAlign: "center"}}>{variant.pages.length ? `${variantPageIdx + 1} / ${variant.pages.length}` : "—"}</Text>
							</PageJumper>
							<Button size="small" disabled={variantPageIdx >= variant.pages.length - 1} onClick={() => changeVariantPage(variantPageIdx + 1)}><RightOutlined/></Button>
							<Tooltip title="Zoom Out"><Button size="small" icon={<ZoomOutOutlined/>} onClick={() => setVariantZoom(z => Math.max(0.1, +(z - 0.1).toFixed(1)))}/></Tooltip>
							<Text style={{fontSize: 11, minWidth: 32, textAlign: "center"}}>{Math.round(variantZoom * 100)}%</Text>
							<Tooltip title="Zoom In"><Button size="small" icon={<ZoomInOutlined/>} onClick={() => setVariantZoom(z => Math.min(2, +(z + 0.1).toFixed(1)))}/></Tooltip>
							<Button size="small" icon={<InsertRowLeftOutlined style={{transform: "rotate(180deg)"}}/>} onClick={() => setVariantSidebarOpen(s => !s)} type={variantSidebarOpen ? "primary" : "default"}>Variante<span className="vpp-btn-count">({variantZoneCount})</span></Button>
						</Space>
					</div>
					<div className="vpp-pane-body">
						<div ref={variantScrollRef} className="vpp-canvas-area">
							<div className="vpp-canvas-wrapper">
								{variantPage && <Text className="vpp-page-label">Variante - Pagina {variantPageIdx + 1}</Text>}
								{variant.pages.length === 0 ? (
									<Empty description="Nessuna pagina variante disponibile. Carica il PDF della variante." style={{marginTop: 60}}>
											<Button type="primary" icon={<CloudUploadOutlined/>} onClick={() => setPdfModalOpen(true)}>Carica PDF</Button>
										</Empty>
								) : (
									<Stage
										ref={variantStageRef}
										width={vStageW} height={vStageH}
										x={STAGE_MARGIN} y={STAGE_MARGIN}
										scaleX={variantZoom} scaleY={variantZoom}
										onMouseDown={handleVMouseDown}
										onMouseMove={handleVMouseMove}
										onMouseUp={handleVMouseUp}
									>
										<Layer listening={false}>
											{variantImage && (
												<KonvaImage name="page-image" image={variantImage} width={vPageW} height={vPageH} perfectDrawEnabled={false}/>
											)}
										</Layer>
										<Layer>
											<Rect ref={previewRectRef} visible={false} listening={false}/>
											{variantImage && variant.zones.filter(z => z.pageIndex === variantPageIdx).map((zone) =>
											{
												const base = getZoneColor(zone, true);
												const dynStyle = {stroke: base.stroke, strokeWidth: rectStrokeWidth, fill: base.fill.replace("1)", `${rectOpacity})`)};
												return (
													<React.Fragment key={zone.id}>
													<TraceableRect
														key={zone.id}
														shapeProps={{
															name: "zone-rect", id: String(zone.id), type: zone.type,
															x: toPreview(zone.x), y: toPreview(zone.y),
															width: toPreview(zone.w), height: toPreview(zone.h)
														}}
														isSelected={selectedIdsSet.has(zone.id)}
														onSelect={(event) =>
													{
														// A differenza della lista per pagina, un risultato può stare su
														// un'altra pagina: selezionarlo e basta lascerebbe la gabbia
														// fuori dallo schermo.
														if (zone.pageIndex !== variantPageIdx) changeVariantPage(zone.pageIndex);
														handleZoneSelect(zone.id, event);
													}}
														onDblClick={(e) => handleZoneToggleStatus(zone.id, e)}
														onContextMenu={(e) => handleZoneContextMenu(zone.id, e)}
														style={dynStyle}
														searchState={getZoneSearchState(searchMatchIds, zone.id)}
														zoom={variantZoom}
														onChange={(np) =>
														{
															// Il vincolo dentro i bordi pagina (della gabbia o dell'intero gruppo
															// spostato/ridimensionato) lo applica queueZoneGeometry insieme alle
															// altre gabbie del gesto.
															queueZoneGeometry(zone.id, {
																x: toFull(np.x), y: toFull(np.y), w: toFull(np.width), h: toFull(np.height)
															});
														}}
													/>
												{/* Nessun gate per type: ManualZoneBadge si auto-nasconde se non ci sono override,
												   così anche le gabbie banner mostrano il tag per banner/banner_collegato/categoria. */}
												<ManualZoneBadge x={toPreview(zone.x)} y={toPreview(zone.y)} overrides={zone.manualOverrides} labels={zone.manualLabels} fieldNames={zone.type === "banner" ? fieldNamesByZoneType.banner : fieldNamesByZoneType.gabbia} fieldColors={fieldColorByKey}/>
												{showSerials && (
													<ZoneSerialBadge
														x={toPreview(zone.x)} y={toPreview(zone.y)} w={toPreview(zone.w)}
														seriale={zone.id}
														color={base.stroke}
														dimmed={getZoneSearchState(searchMatchIds, zone.id) === "dim"}
													/>
												)}
												</React.Fragment>
												);
											})}

											{/* Maniglie della selezione (una o più gabbie), sopra le gabbie */}
											<ZoneSelectionTransformer nodeIds={transformerZoneIds} pageW={vPageW} pageH={vPageH}/>
										</Layer>
									</Stage>
								)}
							</div>
						</div>
						{/* ─── Sidebar gabbie tracciate ─── */}
						<div className="vpp-zones-sidebar vpp-zones-sidebar-right">
							<Input
								className="vpp-zones-search"
								size="small"
								allowClear
								prefix={<SearchOutlined/>}
								placeholder="Cerca seriale o prodotto…"
								value={zoneQuery}
								onFocus={loadCardTexts}
								onChange={(e) => setZoneQuery(e.target.value)}
							/>
							<div className="vpp-zones-list">
								{zoneSearchResults ? (
									zoneSearchResults.length ? (
										<>
											<Text type="secondary" className="vpp-zones-search-count">
												{zoneSearchResults.length} {zoneSearchResults.length === 1 ? "risultato" : "risultati"} in tutta la variante
												{cardTextLoading ? " · carico…" : ""}
											</Text>
											{zoneSearchResults.map((zone) => (
												<ZoneResultItem
													key={zone.id}
													zone={zone}
													isSelected={selectedIdsSet.has(zone.id)}
													description={cardTextBySerial?.get(zone.id)?.label}
													onSelect={(event) =>
													{
														// A differenza della lista per pagina, un risultato può stare su
														// un'altra pagina: selezionarlo e basta lascerebbe la gabbia
														// fuori dallo schermo.
														if (zone.pageIndex !== variantPageIdx) changeVariantPage(zone.pageIndex);
														handleZoneSelect(zone.id, event);
													}}
													onDelete={(id) =>
													{
														variant.setZones(prev => prev.filter(item => item.id !== id));
														setSelectedIds(prev => prev.filter(sid => sid !== id));
													}}
												/>
											))}
										</>
									) : (
										<Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
											description={cardTextLoading ? "Carico…" : "Nessuna gabbia trovata"}
											style={{marginTop: 24}}/>
									)
								) : variantGroupedZones.length ? (
									<Collapse
										accordion
										activeKey={activeZonePanelKey}
										onChange={handleZonePanelChange}
										className="vpp-zones-collapse"
										items={variantGroupedZones.map((group) => ({
											key: String(group.pageIndex),
											label: (
												<div style={{display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%"}}>
													<Text strong>P{group.pageIndex + 1} ({group.zones.length})</Text>
												</div>
											),
											children: (
												<DndContext sensors={dndSensors} collisionDetection={closestCenter}
															onDragEnd={(event) => handlePageDragEnd(group.pageIndex, event)}
															modifiers={[restrictToVerticalAxis]}
												>
													<SortableContext items={group.zones.map(z => z.id)} strategy={verticalListSortingStrategy}>
														<div style={{display: "flex", flexDirection: "column", gap: 6}}>
															{group.zones.map((z) => (
																<SortableZoneItem
																	key={z.id}
																	zone={z}
																	isSelected={selectedIdsSet.has(z.id)}
																	onSelect={(event) => handleZoneSelect(z.id, event)}
																	onDelete={(id) =>
																	{
																		variant.setZones(prev => prev.filter(item => item.id !== id));
																		setSelectedIds(prev => prev.filter(sid => sid !== id));
																	}}
																/>
															))}
														</div>
													</SortableContext>
												</DndContext>
											)
										}))}
									/>
								) : (
									<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessuna gabbia" style={{marginTop: 24}}/>
								)}
							</div>
						</div>
						{variantSidebarOpen && (
							<div className="vpp-pages-sidebar vpp-pages-sidebar-right">
								<div className="vpp-pages-head">
									<Text strong style={{fontSize: 13}}>Pagine</Text>
									<Tooltip title="Aggiungi pagine" placement="left">
										<button
											type="button"
											className="pages-sidebar-add"
											onClick={openAddPages}
											aria-label="Aggiungi pagine"
										>
											<PlusOutlined/>
										</button>
									</Tooltip>
								</div>
								<DndContext
									sensors={dndSensors}
									collisionDetection={closestCenter}
									onDragEnd={handlePagesDragEnd}
									modifiers={[restrictToVerticalAxis]}
								>
									<SortableContext
										items={variant.pages.map((page, idx) => page.name || String(idx))}
										strategy={verticalListSortingStrategy}
									>
										<div className="vpp-pages-list">
											{variant.pages.map((page, idx) =>
											{
												const rawUrl = page.previewUrl || page.url;
												const src = rawUrl ? (rawUrl.startsWith("http") ? rawUrl : `${apiBaseUrl}${rawUrl}`) : "";
												return (
													<SortablePageThumb
														key={page.name || idx}
														page={page}
														index={idx}
														isActive={idx === variantPageIdx}
														zoneCount={variantZonesByPage.get(idx) || 0}
														thumbSrc={src}
														onSelect={changeVariantPage}
														onMenuAction={handlePageMenuAction}
													/>
												);
											})}
										</div>
									</SortableContext>
								</DndContext>
							</div>
						)}
					</div>
					{/* chiude vpp-pane-body */}
				</div>
			</div>

			<UploadPdfModal
				flyerId={variantId}
				open={pdfModalOpen}
				onClose={() => setPdfModalOpen(false)}
				previewScaleDefault={variant.previewScale}
				preserveZones={variant.zones.length > 0}
				onComplete={() =>
				{
					setPdfModalOpen(false);
					setVariantZoom(DEFAULT_SCALE);
					variant.refetch();
				}}
			/>

			<AddPagesModal
				flyerId={variantId}
				open={addPagesOpen}
				pageCount={variant.pages.length}
				onClose={closeAddPages}
				onCompleted={handleAddPagesCompleted}
			/>

			{zoneMenu && (
				<ZoneContextMenu
					x={zoneMenu.x}
					y={zoneMenu.y}
					targetCount={zoneMenu.targetIds.length}
					reprocessCount={zoneMenu.reprocessTargetIds.length}
					fields={zoneMenu.kind === "banner" ? bannerMenuFields : manualFields}
					reprocessDisabled={aiProcessing}
					onReprocess={zoneMenu.reprocessTargetIds.length ? () => handleAiProcessing({serials: zoneMenu.reprocessTargetIds, forceReprocess: true}) : undefined}
					onOpenFields={() => openManualFields(zoneMenu?.targetIds || [], zoneMenu.kind)}
					onEditSerial={zoneMenu.targetIds.length === 1 ? () => setSerialEditZone(variant.zones.find(z => z.id === zoneMenu.targetIds[0]) || null) : undefined}
					editSerialDisabledReason={(() =>
					{
						// Le gabbie ereditate/da rielaborare devono tenere il seriale della sorgente:
						// è la chiave con cui l'import ritrova dati e foto da copiare.
						const z = zoneMenu.targetIds.length === 1 ? variant.zones.find(item => item.id === zoneMenu.targetIds[0]) : null;
						return z && z.status !== "new" ? "Solo le gabbie nuove: le ereditate mantengono il seriale della sorgente" : "";
					})()}
					onClose={() => setZoneMenu(null)}
				/>
			)}

			<EditSerialModal
				open={!!serialEditZone}
				flyerId={variantId}
				zone={serialEditZone}
				localSerials={new Set(variant.zones.filter(z => z.id !== serialEditZone?.id).map(z => z.id))}
				onConfirm={(newSerial) =>
				{
					const oldSerial = serialEditZone.id;
					variant.setZones(prev => prev.map(z => z.id === oldSerial ? {...z, id: newSerial} : z));
					setSelectedIds(prev => prev.map(id => id === oldSerial ? newSerial : id));
					setSerialEditZone(null);
					message.success(`Seriale #${oldSerial} → #${newSerial}`);
				}}
				onCancel={() => setSerialEditZone(null)}
			/>

			<ManualFieldModal
				open={!!manualModal}
				fields={manualModal?.fields || []}
				cooperativaId={variantFlyer?.cooperativaId}
				targetCount={manualModal?.targetIds?.length || 0}
				initialValues={manualModal?.initialValues || {}}
				onApply={handleApplyManual}
				onCancel={() => setManualModal(null)}
			/>

		</div>
	);
}
